import * as API from '@sosocio/creator-types/api';
import type {
	AddObjectModel,
	AddPageObjectModel,
	AddPageObjectModelWithNoStore,
	AddPageObjectModelWithoutNoStore,
	AjaxOptions,
	ChangePageObjectModel,
	ChangePageObjectModelWithIgnorePointerSizeResize,
	ChangePageObjectModelWithNoStore,
	DeselectePageObjectsModel,
	PageObjectModelChangeResult,
	ProductStateGetPageTemplatePositionsAvailableOptions,
	ProductStateGetPageTemplatePositionsOptions,
	ProductStateGetPageTemplateSetOptions,
	ProductStateTemplateSet,
	SVGColorReplacement,
	TemplateSet,
} from '@sosocio/creator-types/app';
import * as DB from '@sosocio/creator-types/database';
import * as PI from '@sosocio/creator-types/project';
import * as svgUtils from '@sosocio/frontend-utils/svg';
import * as urlTools from '@sosocio/frontend-utils/url';
import {
	AxiosRequestConfig,
	AxiosResponse,
} from 'axios';
import ProductStateClass from 'classes/productstate';
import { DEFAULT_DYNAMIC_TEMPLATE_ID } from 'classes/template';
import TemplatePosition from 'classes/templateposition';
import Theme from 'classes/theme';
import EventBus from 'components/event-bus';
import ajax from 'controllers/ajax';
import connector from 'controllers/connector';
import experiment from 'controllers/experiment';
import upload from 'controllers/upload';
import equal from 'deep-equal';
import merge from 'deepmerge';
import loadPhotoObject from 'mutations/pageobject/load';
import loadObjectMask, { type LoadObjectMaskOptions } from 'mutations/pageobject/load-mask';
import removePhotoObjects from 'mutations/product/remove-photo-objects';
import { nanoid } from 'nanoid';
import * as DialogService from 'services/dialog';
import loadImage from 'services/load-image';
import {
	ERRORS_INVALID_REQUEST_DATA,
	ERRORS_INVALID_RETURN_DATA,
	ERRORS_OFFLINE,
} from 'settings/errors';
import {
	MIME_TYPES_BROWSER_SUPPORTED,
	MIME_TYPES_SHOWN_AS_PNG,
	MIME_TYPES_SVG,
} from 'settings/filetypes';
import { OfferingGroups } from 'settings/offerings';
import {
	COVER_BACK,
	COVER_BACK_INSIDE,
	COVER_FRONT,
	COVER_FRONT_INSIDE,
	SPINE,
} from 'settings/values';
import {
	AppDataModule,
	AppStateModule,
	CartItemsModule,
	ConfigModule,
	FontModule,
	PhotosModule,
	ProductsModule,
	ThemeDataModule,
	ThemeStateModule,
	UploadModule,
} from 'store';
import { addArtworkSizesToProductData } from 'tools/artwork-sizes';
import detectBrowser from 'tools/detect-browser';
import getRandomString from 'tools/get-random-string';
import isValidCSSColor from 'tools/is-valid-css-color';
import {
	isLowResolutionPhotoObject,
	shouldApplyUpscaling,
	shouldApplyVectorization,
} from 'tools/low-resolution-warning';
import objDiff from 'tools/object-diff';
import { calculateRotatedBoundingBox } from 'tools/project/geometry-primitives';
import {
	applyPrintColorsToOfferingModel,
	isMultiSidedProduct,
} from 'tools/project/offering';
import { BACKEND_ONLY_FIXED_PAGE_ID_PREFIX } from 'tools/project/page-constants';
import {
	getBackendOnlyFixedPagesCount,
	getPageCountForLimitChecks,
	getPrintedPagesQuantity,
} from 'tools/project/page-count';
import {
	isPageCoverBack,
	isPageCoverFront,
	isPageCoverInside,
	isPageSpine,
} from 'tools/project/page-predicates';
import { insertBackendOnlyFixedPagesForViewing } from 'tools/project/page-viewing';
import { svgAddPrintEffect } from 'tools/project/svg-effects';
import { createTextSVGString } from 'tools/project/text-objects';
import {
	getPageListNormalizationOptionsFromPersistedPageList,
	normalizeViewingPageIdsForPageList,
	type NormalizeViewingPageIdsOptions,
	resolvePersistedPageIdFromViewingPageId,
} from 'tools/project/viewing-page-ids';
import _ from 'underscore';
import {
	getFontFamily,
	getTextObjectFontSize,
	MIN_FONTSIZE,
	pixelToPoint,
} from 'utils/font';
import { isPhotoProcessingErrorRetryable } from 'utils/photo-processing-error';
import reportError from 'utils/report-error';
import { fit } from 'utils/text';
import { reactive } from 'vue';
import {
	Action,
	Module,
	Mutation,
	VuexModule,
} from 'vuex-module-decorators';
import defaults from './defaults';
import {
	copyProductStateForSerialization,
	copyPropertiesExcluding,
} from './serialization';
import { applyVectorSVGPrintEffect } from './vector-svg';

type PageBackgroundChange = Partial<Pick<PI.PageModel, '_bgpattern' | 'bgcolor' | 'bgpattern'>>;

type ProductStateHistoryRestoreType = 'undo' | 'redo';

interface LoadPageObjectPhotoOptionsBase {
	/**
	 * When set, applies the print effect color replacement to vector-based photos.
	 */
	addPrintEffect?: boolean;
	/**
	 * When true, starts a fresh load even if another request is already in-flight for the same key.
	 */
	forceReload?: boolean;
	/**
	 * Forces the loader to use the remote source even when a local one is available.
	 */
	forceRemote?: boolean;
	/**
	 * Indicates whether the object mask should be generated together with the image.
	 */
	includeMask?: boolean;
	/**
	 * Offering model to use while loading; defaults to the current {@link DB.OfferingModel}.
	 */
	offeringModel?: DB.OfferingModel | null;
	/**
	 * Color applied to the print effect when {@link LoadPageObjectPhotoOptionsBase.addPrintEffect} is true.
	 */
	printEffectColor?: string;
	/**
	 * Resolution passed through to the loader.
	 */
	resolution?: 'thumb' | 'low' | 'high';
	/**
	 * Scaling factor forwarded to the loader when applicable.
	 */
	scaling?: number;
}

interface LoadPageObjectPhotoOptionsWithNoStore extends LoadPageObjectPhotoOptionsBase {
	/**
	 * Identifier of the {@link PI.PageObjectModel} to load.
	 */
	id: PI.PageObjectModel['id'];
	/**
	 * Flag indicating that the provided {@link LoadPageObjectPhotoOptionsWithNoStore.originalPageObject} should be mutated directly.
	 */
	noStore: true;
	/**
	 * Page object instance to mutate when {@link LoadPageObjectPhotoOptionsWithNoStore.noStore} is true.
	 */
	originalPageObject: PI.PageObjectModel;
}

interface LoadPageObjectPhotoOptionsWithStore extends LoadPageObjectPhotoOptionsBase {
	/**
	 * Identifier of the {@link PI.PageObjectModel} to load.
	 */
	id: PI.PageObjectModel['id'];
	/**
	 * Flag indicating store-backed mutation. Explicitly remains false to match overload expectations.
	 */
	noStore?: false;
}

type LoadPageObjectPhotoOptions = LoadPageObjectPhotoOptionsWithNoStore | LoadPageObjectPhotoOptionsWithStore;

interface ResolvePersistedPageIdState {
	pageList: PI.PageModel['id'][];
	pages: Record<string, PI.PageModel>;
	spine: PI.PageModel | null;
}

/**
 * Fill missing photo dimensions through a browser-safe source when available.
 *
 * Conversion-only local formats deliberately skip browser decoding; their
 * dimensions arrive with the processed remote model.
 *
 * @param data Partial photo model being selected.
 * @returns The same model with dimensions added when inspection succeeds.
 */
function getPhotoSize(data: Partial<PI.PhotoModel>): Promise<Partial<PI.PhotoModel>> {
	if (data.source == 'dropbox'
		&& data.full_url
		&& data.full_url.charAt(0) === '/'
	) {
		if (!connector.networks.dropbox.bridge.getFullUrl) {
			throw new Error('Missing required bridge function getFullUrl');
		}

		return connector.networks.dropbox.bridge.getFullUrl(data.full_url)
			.then((URL) => loadImage(URL))
			.catch(() => false)
			.then((returnData) => {
				if (typeof returnData === 'object') {
					data.full_width = returnData.image.width;
					data.full_height = returnData.image.height;
				}
				return data;
			});
	}

	if (data.full_width
		&& data.full_height
	) {
		return Promise.resolve(data);
	}

	if (
		data._localRef
		&& (
			!data._type
			|| MIME_TYPES_BROWSER_SUPPORTED.includes(data._type)
		)
	) {
		const localFile = upload.getLocalFile(
			data._localRef,
		);

		if (localFile) {
			return loadImage(
				localFile,
				{
					failureDiagnostics: {
						declaredMime: (
							localFile.type
							|| 'unknown'
						),
						resolvedMime: data._type,
						stage: 'photo-size',
						uploadId: data._localRef,
					},
				},
			)
				.catch(() => false)
				.then((returnData) => {
					if (typeof returnData === 'object') {
						data.full_width = returnData.image.width;
						data.full_height = returnData.image.height;
					}
					return data;
				});
		}
	}

	if (typeof data.full_url === 'string'
		&& data.full_url.length
	) {
		return loadImage(data.full_url)
			.catch(() => false)
			.then((returnData) => {
				if (typeof returnData === 'object') {
					data.full_width = returnData.image.width;
					data.full_height = returnData.image.height;
				}
				return data;
			});
	}

	return Promise.resolve(data);
}

function hasShapeMask(mask?: PI.PageObjectModel['mask']): boolean {
	return !!mask
		&& mask !== 'none'
		&& mask !== 'square';
}

/**
 * Generates a unique key representing the requested photo load operation.
 *
 * @param objectId Identifier of the {@link PI.PageObjectModel}.
 * @param options Collection of option values impacting the result.
 * @returns Deterministic key that can be used to coalesce in-flight loads.
 */
function buildPageObjectPhotoLoadKey(
	objectId: PI.PageObjectModel['id'],
	options: {
		addPrintEffect: boolean;
		forceRemote: boolean;
		includeMask: boolean;
		offeringModelId?: DB.OfferingModel['id'] | null;
		printEffectColor?: string;
		resolution: 'thumb' | 'low' | 'high';
		scaling?: number;
	},
): string {
	const resolutionPart = options.resolution;
	const scalingPart = (
		typeof options.scaling === 'number'
			? options.scaling
			: 'auto'
	);
	const printEffectPart = options.addPrintEffect ? 'print' : 'plain';
	const printColorPart = options.printEffectColor ?? 'none';
	const offeringPart = options.offeringModelId ?? 'none';
	const maskPart = options.includeMask ? 'mask' : 'no-mask';
	const remotePart = options.forceRemote ? 'remote' : 'auto';

	return [
		objectId,
		resolutionPart,
		scalingPart,
		printEffectPart,
		printColorPart,
		offeringPart,
		maskPart,
		remotePart,
	].join('|');
}

function getPageListNormalizationOptions(
	state: ResolvePersistedPageIdState,
): NormalizeViewingPageIdsOptions {
	return getPageListNormalizationOptionsFromPersistedPageList(
		state.pageList,
		state.pages,
	);
}

function resolvePersistedPageId(
	state: ResolvePersistedPageIdState,
	pageId: PI.PageModel['id'],
): PI.PageModel['id'] | null {
	if (pageId === SPINE) {
		return state.spine?.id ?? null;
	}

	return resolvePersistedPageIdFromViewingPageId(
		pageId,
		getPageListNormalizationOptions(state),
	);
}

@Module({
	namespaced: true,
	name: 'productstate',
})
export default class ProductState extends VuexModule implements PI.ProductStateModel {
	// Reference to the page currently being edited
	_activePage: PI.PageModel['id'] | null = null;

	// Bookmarks for page indexes shown in the product preview
	_bookmarks = reactive<PI.BookmarksModel>({
		left: 0,
		right: 1,
	});

	// Marker indicating the current position in the _snapshots array
	_historyIndex = -1;

	// Maximum number of historic snapshots
	_historyLimit = 20;

	// Flag to indicate if the mask is loading
	_loadingMask = false;

	/* Flag to indicate if the offering frame image is loading */
	_loadingOfferingFrame = false;

	// Flag to indicate if the overlay is loading
	_loadingOverlay = false;

	// Store the loaded overlay image for the product
	_mask: HTMLImageElement | null = null;

	// Store the loaded overlay image for the product
	_overlay: HTMLImageElement | null = null;

	// Flag to indicate if the product has been updated since the last save
	_saved: boolean | null = false;

	_snapshots = reactive<string[]>([]);

	_thinLineWarningPageIds = reactive<PI.PageModel['id'][]>([]);

	/**
	 * Tracks in-flight photo load promises keyed by the load options signature.
	 */
	private _pageObjectPhotoLoadPromises = reactive(new Map<string, Promise<PI.PageObjectModel>>());

	/**
	 * Maintains monotonic counters so outdated loads can be ignored safely.
	 */
	private _pageObjectPhotoLoadVersions = reactive(new Map<string, number>());

	address: PI.AddressModel | null = null;

	customAttributes = reactive<NonNullable<PI.ProductDataModel['customAttributes']>>({});

	metaData = reactive<NonNullable<PI.ProductDataModel['metaData']>>({
		softwareWebVersions: [],
		softwarePlatforms: [],
	});

	objects = reactive<Record<string, PI.PageObjectModel>>({});

	pageList = reactive<DB.PageModel['id'][]>([]);

	pages = reactive<Record<DB.PageModel['id'], PI.PageModel>>({});

	photoList = reactive<PI.PhotoModel['id'][]>([]);

	productAttributes = reactive<PI.ProductAttributeModel[]>([]);

	productSettings = reactive<PI.ProductSettings>({
		autoFillMethod: 'photodate',
		bgcolor: null,
		bgpattern: null,
		childOfferingTypeId: null,
		childOfferingVariantId: null,
		faceDetect: true,
		startWithOfferingOptionUI: null,
	});

	productTours = reactive<PI.ProductDataModel['productTours']>({});

	productId: number | null = null;

	thinLineDetected = false;

	version: number | null = null;

	spine: PI.PageModel | null = null;

	public get findPageFromObject() {
		return (objectModelId: PI.PageObjectModel['id']): PI.PageModel | undefined => this.getPages.find(
			(model) => model.objectList && model.objectList.indexOf(objectModelId) >= 0,
		);
	}

	public get getThinLineWarningPageStates(): Record<string, boolean> {
		return this._thinLineWarningPageIds.reduce<Record<string, boolean>>(
			(pageWarningStates, pageId) => {
				pageWarningStates[String(pageId)] = true;

				return pageWarningStates;
			},
			{},
		);
	}

	public get getThinLineWarningPageIds(): PI.PageModel['id'][] {
		return [...this._thinLineWarningPageIds];
	}

	public get findPhoto() {
		return (modelData: Partial<PI.PhotoModel>): PI.PhotoModel | null => {
			if (modelData.id) {
				if (this.hasSavedPhoto(modelData.id)) {
					return this.context.rootGetters['photos/getById'](modelData.id);
				}
			} else if (modelData.source && modelData.externalId) {
				return this.context.rootGetters['photos/findWhere']({
					source: modelData.source,
					externalId: String(modelData.externalId),
				});
			}
			return null;
		};
	}

	public get getActivePage() {
		if (
			this._activePage
			&& this._activePage === this.getSpineForViewing?.id
		) {
			return (
				this.spine
				?? undefined
			);
		}

		return this._activePage
			? this.pages[this._activePage] || undefined
			: undefined;
	}

	public get getAddress() {
		return this.address;
	}

	public get getAttribute() {
		return (propertyName: string) => _.findWhere(
			this.productAttributes,
			{
				property: propertyName,
			},
		);
	}

	public get getAttributes() {
		return this.productAttributes;
	}

	public get getBookmarks() {
		return this._bookmarks;
	}

	public get getChildOfferingId() {
		if (!this.getOffering) {
			return undefined;
		}

		if (this.productSettings.childOfferingTypeId === 'bestFit'
			|| !this.productSettings.childOfferingTypeId
			|| !this.productSettings.childOfferingVariantId
		) {
			return undefined;
		}

		const offeringId = parseInt(
			`${this.getOffering.groupid}${this.productSettings.childOfferingTypeId}${this.productSettings.childOfferingVariantId || 1}`,
			10,
		);
		return offeringId;
	}

	public get getCoverPage() {
		const productModel = this.getProduct;

		let pageIndex = 1;

		if (productModel) {
			if (OfferingGroups(
				productModel.group,
				['PhotoFrameBox'],
			)) {
				pageIndex = 0;
			} else if (OfferingGroups(
				productModel.group,
				['PrintTypes'],
			)) {
				const offeringModel = this.context.rootGetters['appdata/findOfferingWhere']({
					groupid: productModel.group,
					typeid: productModel.typeid,
				});
				pageIndex = offeringModel.hasback ? 1 : 0;
			} else if (OfferingGroups(
				productModel.group,
				['BasicProducts'],
			)) {
				pageIndex = 0;
			}
		}

		return this.getPageByNumber(pageIndex);
	}

	public get getCroppedPages() {
		const pageModels = _.where(
			this.getPages,
			{
				editable: 1,
			},
		);
		const cropThreshold = this.context.rootState.config['cropModule.threshold'];

		return pageModels.filter(
			(pageModel) => {
				const croppedPhotoObjects = this.getPageObjects(pageModel).filter(
					(pageObjectModel) => pageObjectModel.type === 'photo'
						&& (
							pageObjectModel.cropwidth < cropThreshold * pageObjectModel.maxwidth
							|| pageObjectModel.cropheight < cropThreshold * pageObjectModel.maxheight
						),
				);

				return croppedPhotoObjects.length;
			},
		);
	}

	public get getData(): PI.ProductDataModel {
		const data: PI.ProductDataModel = JSON.parse(JSON.stringify(
			copyProductStateForSerialization(this.context.state as unknown as PI.ProductStateModel),
		));
		const productModel = this.getProduct;

		if (productModel) {
			// Set filtered product properties to data
			const unwantedProductProperties: Array<keyof DB.ProductModel> = [
				'deleted',
				'graphinstance',
				'height',
				'shared',
				'thumbnail',
				'userid',
				'width',
			];
			const productData = JSON.parse(JSON.stringify(
				copyPropertiesExcluding(
					productModel,
					unwantedProductProperties,
				),
			)) as DB.ProductModel;
			const dataProductKeys = Object.keys(productData) as (keyof PI.ProductDataModel['product'])[];

			data.product = dataProductKeys.reduce(
				(newProductData, key) => {
					if (unwantedProductProperties.includes(key)) {
						return newProductData;
					}

					return {
						...newProductData,
						[key]: productData[key],
					};
				},
				{} as DB.ProductModel,
			);
		}

		// Filter top level properties
		const topLevelKeys = Object.keys(data) as (keyof PI.ProductDataModel)[];

		for (const key of topLevelKeys) {
			if (key.charAt(0) === '_') {
				delete data[key];
			}
		}

		// Filter page properties
		const pages = Object.values(data.pages);

		for (const pageModel of pages) {
			const pageLevelKeys = Object.keys(pageModel) as (keyof PI.PageModel)[];

			for (const key of pageLevelKeys) {
				if (key.charAt(0) === '_') {
					delete pageModel[key];
				}
			}
		}

		// Filter object properties
		const objects = Object.values(data.objects);

		for (const objectModel of objects) {
			const objectLevelKeys = Object.keys(objectModel) as (keyof PI.PageObjectModel)[];

			for (const key of objectLevelKeys) {
				if (key.charAt(0) === '_') {
					delete objectModel[key];
				}
			}
		}

		if (data.spine) {
			const pageLevelKeys = Object.keys(data.spine) as (keyof PI.PageModel)[];

			for (const key of pageLevelKeys) {
				if (key.charAt(0) === '_') {
					delete data.spine[key];
				}
			}
		}

		return data;
	}

	public get getDataForSnapshot() {
		const data = this.getData;

		// Filter out unsaved photo models
		data.photoList = data.photoList.filter((id) => (
			typeof id !== 'string'
		));

		// Filter out object models that are linked to unsaved photo models
		const objects: Record<string, any> = {};
		Object
			.values(data.objects)
			.forEach((objectModel) => {
				if (
					!objectModel.photoid
					|| data.photoList.indexOf(objectModel.photoid) >= 0
				) {
					objects[objectModel.id] = objectModel;
				}
			});
		data.objects = objects;
		const pageListNormalizationOptions = getPageListNormalizationOptionsFromPersistedPageList(
			data.pageList,
			data.pages,
		);
		data.pageList = normalizeViewingPageIdsForPageList(
			data.pageList,
			pageListNormalizationOptions,
		);
		const pageIdsToRemove: DB.PageModel['id'][] = [];

		// Filter out unsaved object models from the page models
		data.pageList.forEach((pageId) => {
			const pageData = data.pages[pageId];

			if (!pageData) {
				pageIdsToRemove.push(pageId);
				reportError(
					new Error('Page model is missing from product data'),
					'warning',
					(event) => {
						event.addMetadata(
							'productData',
							{
								metaData: data.metaData,
								pageId,
								pageList: data.pageList,
								product: data.product,
								version: data.version,
							},
						);
					},
				);
				return;
			}

			if (pageData.objectList) {
				if (!Array.isArray(pageData.objectList)) {
					// Automatically fix corrupt data
					pageData.objectList = Object.values(pageData.objectList);
				}

				data.pages[pageId].objectList = pageData.objectList.filter(
					(objectId) => objectId in data.objects,
				);
			}
		});

		/**
		 * Remove pages that are missing from the product data
		 */
		for (const pageId of pageIdsToRemove) {
			const index = data.pageList.indexOf(pageId);

			if (index >= 0) {
				data.pageList.splice(
					index,
					1,
				);
			}
		}

		// Store used software versions and platforms in project meta data
		if (!data.metaData) {
			data.metaData = {
				softwareWebVersions: [],
				softwarePlatforms: [],
			};
		}
		if (!data.metaData.softwareWebVersions.includes(VERSION)) {
			data.metaData.softwareWebVersions.push(VERSION);
		}
		if (data.metaData.softwareWebVersions.length > 10) {
			// This fixes the issue of the softwareWebVersions array growing indefinitely
			data.metaData.softwareWebVersions = data.metaData.softwareWebVersions.slice(
				-10,
			);
		}

		let softwarePlatform;

		if (window.glPlatform === 'web') {
			softwarePlatform = JSON.stringify(detectBrowser());
		} else if (window.glPlatform === 'native') {
			const deviceDetails = _.omit(
				window.nativeDeviceDetails,
				'deviceUUID',
			);
			softwarePlatform = JSON.stringify(deviceDetails);
		}
		if (softwarePlatform
			&& !data.metaData.softwarePlatforms.includes(softwarePlatform)
		) {
			data.metaData.softwarePlatforms.push(softwarePlatform);
		}
		if (data.metaData.softwarePlatforms.length > 10) {
			// This fixes the issue of the softwarePlatforms array growing indefinitely
			data.metaData.softwarePlatforms = data.metaData.softwarePlatforms.slice(
				-10,
			);
		}

		return data;
	}

	public get getEditablePages(): PI.PageModel[] {
		const pageModels = this.getPages.filter((pageModel) => pageModel.editable);

		const {
			originalGroupId,
			originalTypeId,
			printPositions,
		} = this.getProductSettings;
		const offeringModel = this.getOffering;

		if (
			!printPositions?.length
			|| !offeringModel
			|| typeof originalGroupId === 'undefined'
			|| typeof originalTypeId === 'undefined'
			|| offeringModel.groupid !== originalGroupId
			|| offeringModel.typeid !== originalTypeId
		) {
			return pageModels;
		}

		return pageModels.filter((pageModel) => (
			printPositions.includes(this.pageList.indexOf(pageModel.id))
		));
	}

	/**
	 * Count of backend-only fixed pages configured in theme data for book products.
	 */
	public get getBackendOnlyFixedPagesCount(): number {
		return getBackendOnlyFixedPagesCount(
			this.getOffering,
			ThemeStateModule.getFixedPages,
		);
	}

	public get getOffering(): DB.OfferingModel | null {
		const productModel = this.getProduct;

		if (productModel) {
			const searchProps: Partial<DB.OfferingModel> = {
				groupid: productModel.group,
				typeid: productModel.typeid,
			};

			if (productModel.variantid) {
				searchProps.variantid = productModel.variantid;
			}

			const offeringModel = AppDataModule.findOfferingWhere(searchProps);

			if (!offeringModel) {
				return null;
			}

			return applyPrintColorsToOfferingModel(
				offeringModel,
				this.getProductSettings,
			);
		}

		return null;
	}

	public get getMaskImage() {
		return this._mask;
	}

	public get getObjects(): PI.PageObjectModel[] {
		const models: PI.PageObjectModel[] = [];

		if (typeof this.objects === 'object') {
			const objectModels = Object.values(this.objects);

			for (const objectModel of objectModels) {
				if (objectModel) {
					models.push(objectModel);
				}
			}
		}

		return models.sort(
			(a, b) => a.z_axis - b.z_axis,
		);
	}

	public get getOverlayImage() {
		return this._overlay;
	}

	public get getPageCount(): number | null {
		const offeringModel = this.getOffering;

		if (offeringModel) {
			if (
				OfferingGroups(
					offeringModel.groupid,
					['BookTypes'],
				)
			) {
				// We extract the front- and back cover pages
				return this.getPages.length - 2;
			}

			if (!OfferingGroups(
				offeringModel.groupid,
				['BasicProducts'],
			)) {
				return this.getEditablePages.length;
			}

			return this.getPages.length;
		}

		return null;
	}

	public get getPage() {
		return (id: PI.PageModel['id']) => {
			const persistedPageId = resolvePersistedPageId(
				this,
				id,
			);

			return (
				this.spine?.id === persistedPageId
					? this.spine
					: this.pages[persistedPageId ?? id]
			);
		};
	}

	public get getPageByNumber() {
		return (pageIndex: number): PI.PageModel | undefined => {
			const pageId = this.pageList[pageIndex];

			if (pageId) {
				return this.getPage(pageId);
			}

			return undefined;
		};
	}

	public get getEditablePageByNumber(): (pageIndex: number) => PI.PageModel | undefined {
		return (pageIndex) => {
			const {
				originalGroupId,
				originalTypeId,
				printPositions,
			} = this.getProductSettings;
			const offeringModel = this.getOffering;

			if (
				!printPositions?.length
				|| !offeringModel
				|| typeof originalGroupId === 'undefined'
				|| typeof originalTypeId === 'undefined'
				|| offeringModel.groupid !== originalGroupId
				|| offeringModel.typeid !== originalTypeId
			) {
				return this.getPageByNumber(pageIndex);
			}

			const pageId = this.getEditablePages[pageIndex]?.id;

			if (pageId) {
				return this.getPage(pageId);
			}

			return undefined;
		};
	}

	public get getPageIndex(): (pageModel: OptionalExceptFor<PI.PageModel, 'id'>) => number {
		return (pageModel) => {
			const persistedPageId = resolvePersistedPageId(
				this,
				pageModel.id,
			);

			return (
				persistedPageId
					? this.pageList.indexOf(persistedPageId)
					: -1
			);
		};
	}

	public get getEditablePageIndex(): (pageModel: PI.PageModel) => number {
		return (pageModel) => {
			const {
				originalGroupId,
				originalTypeId,
				printPositions,
			} = this.getProductSettings;
			const offeringModel = this.getOffering;

			if (
				!printPositions?.length
				|| !offeringModel
				|| typeof originalGroupId === 'undefined'
				|| typeof originalTypeId === 'undefined'
				|| offeringModel.groupid !== originalGroupId
				|| offeringModel.typeid !== originalTypeId
			) {
				return this.getPageIndex(pageModel);
			}

			return this.getEditablePages.indexOf(pageModel);
		};
	}

	public get getPageLabel(): (
		pageModel: OptionalExceptFor<PI.PageModel, 'id'>,
		pageIndex?: number,
		offeringModel?: OptionalExceptFor<DB.OfferingModel, 'groupid'> | null,
		leadingZero?: boolean,
		pageModels?: PI.PageModel[],
	) => number | string | null {
		return (
			pageModel,
			pageIndex = this.getPageIndex(pageModel),
			offeringModel = this.getOffering,
			leadingZero = true,
			pageModels = this.getPages,
		) => {
			if (offeringModel) {
				if (isMultiSidedProduct(this.getOffering)) {
					const translationKey = `offerings:${offeringModel.id}.pages.${pageIndex}`;

					if (window.App.router.$i18next.exists(translationKey)) {
						return window.App.router.$t(translationKey);
					}
				}

				if (
					isPageSpine(
						offeringModel,
						pageModel.id,
						this.getSpine?.id,
					)
				) {
					return window.App.router.$t('spine');
				}

				if (
					isPageCoverFront(
						offeringModel,
						pageModel.id,
						pageIndex,
					)
				) {
					return window.App.router.$t('cover_front');
				}

				if (
					isPageCoverInside(
						offeringModel,
						pageModel.id,
					)
				) {
					return window.App.router.$t('coverInside');
				}

				if (
					isPageCoverBack(
						offeringModel,
						pageModel.id,
						pageIndex,
					)
				) {
					return window.App.router.$t('cover_back');
				}

				if (
					OfferingGroups(
						offeringModel.groupid,
						'CardGame',
					)
				) {
					return pageIndex;
				}

				if (
					OfferingGroups(
						offeringModel.groupid,
						'BookTypes',
					)
				) {
					let effectivePageIndex: number;

					if (pageIndex >= 0) {
						effectivePageIndex = pageIndex;
					} else {
						({ pageIndex: effectivePageIndex } = this.getPagesForViewing.reduce(
							(memo, pageModelForViewing) => {
								if (memo.found) {
									return memo;
								}

								if (pageModelForViewing.id === pageModel.id) {
									return {
										pageIndex: memo.pageIndex + 1,
										found: true,
									};
								}

								/**
								 * If there are adjacent backend-only fixed pages, this will mean that the current
								 * page being iterated over will return `-1` as its index, so for these cases we use
								 * the previous index + 1 as the effective page index.
								 */
								let newPageIndex = this.getPageIndex(pageModelForViewing);

								if (newPageIndex < 0) {
									newPageIndex = memo.pageIndex + 1;
								}

								return {
									pageIndex: newPageIndex,
									found: false,
								};
							},
							{
								pageIndex: -1,
								found: false,
							},
						));
					}

					const pageLabel = effectivePageIndex - 1;

					if (
						pageLabel < 10
						&& leadingZero
					) {
						return `0${pageLabel}`;
					}

					return pageLabel;
				}

				if (
					OfferingGroups(
						offeringModel.groupid,
						'Cards',
					)
				) {
					return pageIndex + 1;
				}

				if (
					OfferingGroups(
						offeringModel.groupid,
						'DoubleSidePrints',
					)
				) {
					const firstPage = pageModels[0];

					return (pageIndex + 1) / (!firstPage.editable ? 2 : 1);
				}

				if (
					OfferingGroups(
						offeringModel.groupid,
						'PrintTypes',
					)
				) {
					const firstPage = pageModels[0];
					const pageLabel = (pageIndex + 1) / (offeringModel.hasback && !firstPage.editable ? 2 : 1);

					if (
						pageLabel < 10
						&& leadingZero
					) {
						return `0${pageLabel}`;
					}

					return pageLabel;
				}
			}

			return null;
		};
	}

	public get getPageMaxZ(): (pageModel: PI.PageModel) => number {
		return (pageModel) => (
			this
				.getPageObjects(pageModel)
				.reduce(
					(memo, objectModel) => {
						if (objectModel.editable) {
							return Math.max(
								objectModel.z_axis,
								memo,
							);
						}

						return memo;
					},
					1,
				)
		);
	}

	public get getPageObject() {
		return (id: PI.PageObjectModel['id']) => this.objects[id];
	}

	public get getPageObjects() {
		return (pageModel: PI.PageModel) => {
			const models: PI.PageObjectModel[] = [];

			if (
				pageModel.objectList
				&& Array.isArray(pageModel.objectList)
			) {
				for (const objectId of pageModel.objectList) {
					const objectModel = this.objects[objectId];

					if (objectModel) {
						models.push(objectModel);
					}
				}
			}

			if (
				models.length === 0
				&& typeof pageModel.id === 'string'
				&& pageModel.id.startsWith(BACKEND_ONLY_FIXED_PAGE_ID_PREFIX)
			) {
				const fixedPageStateModel = ThemeStateModule.getFixedPages.find(
					(pageStateModel) => `${BACKEND_ONLY_FIXED_PAGE_ID_PREFIX}${pageStateModel.id}` === pageModel.id,
				);

				if (fixedPageStateModel) {
					models.push(
						...fixedPageStateModel.objects,
					);
				}
			}

			return models.sort((a, b) => a.z_axis - b.z_axis);
		};
	}

	public get getPagePhotos(): (pageModel: PI.PageModel) => PI.PhotoModel[] {
		return (pageModel) => {
			const objectModels = this.getPageObjects(pageModel);
			const photoModels = objectModels
				.reduce(
					(photoModelsArray, objectModel) => {
						if (objectModel.photoid) {
							const photoModel = PhotosModule.getById(objectModel.photoid);

							if (
								photoModel
								&& !photoModelsArray.includes(photoModel)
							) {
								photoModelsArray.push(photoModel);
							}
						}

						return photoModelsArray;
					},
					[] as PI.PhotoModel[],
				);

			return photoModels;
		};
	}

	public get getPages(): PI.PageModel[] {
		return this.pageList
			.map((pageId) => this.pages[pageId])
			.filter((pageModel) => !!pageModel);
	}

	public get getSpine(): PI.PageModel | undefined {
		return (
			this.spine
			?? undefined
		);
	}

	public get getSpineForViewing(): PI.PageModel | undefined {
		if (this.spine) {
			return {
				...this.spine,
				id: SPINE,
				width: Math.max(
					this.spine.width,
					Math.round(this.spine.height * 0.15),
				),
			};
		}

		return undefined;
	}

	private get getLinkedOfferingOptionValueModel() {
		return (
			offeringOptionId: DB.OfferingOptionModel['id'],
			offeringId: DB.OfferingModel['id'],
		): DB.OfferingOptionValueModel | undefined => {
			// Get all the possible values for the coverColor offering option
			const valueModels = AppDataModule.offeringoptionvalues.filter(
				(m) => m.offeringoptionid === offeringOptionId,
			);

			// Get the ids of the possible values for the coverColor offering option
			const valueModelIds = valueModels.map(
				(valueModel) => valueModel.id,
			);

			// Find the link model that links the selected value for the coverColor to the current offering
			const linkModel = AppDataModule.offeringoptionvalueofferinglinks.find(
				(m) => m.offeringid === offeringId && valueModelIds.includes(m.offeringoptionvalueid),
			);

			// Find the value model that is linked to the current offering
			return valueModels.find(
				(m) => m.id === linkModel?.offeringoptionvalueid,
			);
		};
	}

	/**
	 * Builds viewing-only pages, including book cover surfaces and special-product filtering.
	 *
	 * @returns Page models transformed for display without changing persisted page state.
	 */
	public get getPagesForViewing(): PI.PageModel[] {
		const offeringModel = this.getOffering;

		if (!offeringModel) {
			return [];
		}

		const pageModels = (
			offeringModel.hasback
				? this.getPages
					.slice(0)
					.filter((pageModel) => (
						pageModel.editable === 1
					))
				: this.getPages.slice(0)
		);

		let coverBackgroundColor = '#FFFFFF';
		let insideCoverBackgroundColor = '#FFFFFF';
		let coverBackgroundPattern = null;
		let frontCoverImage = null;
		let insideFrontCoverImage = null;
		let backCoverImage = null;
		let insideBackCoverImage = null;

		if (document.documentElement) {
			const cssBgColor = getComputedStyle(document.documentElement)
				.getPropertyValue('--photobook-non-editable-page-background')
				.trim();

			if (isValidCSSColor(cssBgColor)) {
				insideCoverBackgroundColor = cssBgColor;
			}
		}

		const coverOptionModels: DB.OfferingOptionModel[] = (
			OfferingGroups(
				offeringModel.groupid,
				'BookTypes',
			)
				? AppDataModule.getOfferingOptionModels(offeringModel.id)
				: []
		);

		// Check to see if there's a cover color and/or pattern defined for this offering
		const coverColorOptionModel = coverOptionModels
			.find((optionModel) => (
				optionModel.tag === 'cover-color'
			));

		if (coverColorOptionModel) {
			const valueModel = this.getLinkedOfferingOptionValueModel(
				coverColorOptionModel.id,
				offeringModel.id,
			);

			// We can read the requested bgcolor from the tag property of the value model
			if (
				valueModel?.tag
				&& isValidCSSColor(valueModel.tag)
			) {
				coverBackgroundColor = valueModel.tag;
			}

			if (
				valueModel?.image
				&& valueModel.image.substring(
					0,
					4,
				) === 'http'
			) {
				coverBackgroundPattern = valueModel.image;
			}
		}

		// Check to see if there's a front cover image defined for this offering
		const frontCoverImageOptionModel = coverOptionModels
			.find((optionModel) => (
				optionModel.tag === 'front-cover-image'
			));

		if (frontCoverImageOptionModel) {
			const valueModel = this.getLinkedOfferingOptionValueModel(
				frontCoverImageOptionModel.id,
				offeringModel.id,
			);

			if (
				valueModel?.image
				&& valueModel.image.substring(
					0,
					4,
				) === 'http'
			) {
				frontCoverImage = valueModel.image;
			}
		}

		// Check to see if there's a inside front cover image defined for this offering
		const insideFrontCoverImageOptionModel = coverOptionModels
			.find((optionModel) => (
				optionModel.tag === 'inside-front-cover-image'
			));

		if (insideFrontCoverImageOptionModel) {
			const valueModel = this.getLinkedOfferingOptionValueModel(
				insideFrontCoverImageOptionModel.id,
				offeringModel.id,
			);

			if (
				valueModel?.image
				&& valueModel.image.substring(
					0,
					4,
				) === 'http'
			) {
				insideFrontCoverImage = valueModel.image;
			}
		}

		// Check to see if there's a back cover image defined for this offering
		const backCoverImageOptionModel = coverOptionModels
			.find((optionModel) => (
				optionModel.tag === 'back-cover-image'
			));

		if (backCoverImageOptionModel) {
			const valueModel = this.getLinkedOfferingOptionValueModel(
				backCoverImageOptionModel.id,
				offeringModel.id,
			);

			if (
				valueModel?.image
				&& valueModel.image.substring(
					0,
					4,
				) === 'http'
			) {
				backCoverImage = valueModel.image;
			}
		}

		// Check to see if there's a inside back cover image defined for this offering
		const insideBackCoverImageOptionModel = coverOptionModels
			.find((optionModel) => (
				optionModel.tag === 'inside-back-cover-image'
			));

		if (insideBackCoverImageOptionModel) {
			const valueModel = this.getLinkedOfferingOptionValueModel(
				insideBackCoverImageOptionModel.id,
				offeringModel.id,
			);

			if (
				valueModel?.image
				&& valueModel.image.substring(
					0,
					4,
				) === 'http'
			) {
				insideBackCoverImage = valueModel.image;
			}
		}

		if (offeringModel.fixedcover) {
			// Replace the back cover page with a virtual one that shows the fixed cover
			pageModels.splice(
				0,
				1,
				{
					id: COVER_BACK,
					width: offeringModel.width,
					height: offeringModel.height,
					offset: offeringModel.bleedmargin,
					bgcolor: coverBackgroundColor,
					bgimage: backCoverImage,
					bgpattern: coverBackgroundPattern,
					editable: 0,
					movable: 0,
					quantity: 1,
					scaling: 1,
					template: null,
					thumbnail: null,
					preview: null,
				},
			);

			// Replace the front cover page with a virtual one that shows the fixed cover
			pageModels.splice(
				1,
				1,
				{
					id: COVER_FRONT,
					width: offeringModel.width,
					height: offeringModel.height,
					offset: offeringModel.bleedmargin,
					bgcolor: coverBackgroundColor,
					bgimage: frontCoverImage,
					bgpattern: coverBackgroundPattern,
					editable: 0,
					movable: 0,
					quantity: 1,
					scaling: 1,
					template: null,
					thumbnail: null,
					preview: null,
				},
			);
		}

		if (
			offeringModel.startright
			&& pageModels.length
		) {
			// Add a virtual page for the inside of the front cover
			pageModels.splice(
				2,
				0,
				{
					id: COVER_FRONT_INSIDE,
					width: offeringModel.width,
					height: offeringModel.height,
					offset: offeringModel.bleedmargin,
					bgcolor: insideCoverBackgroundColor,
					bgimage: insideFrontCoverImage,
					bgpattern: coverBackgroundPattern,
					editable: 0,
					movable: 0,
					quantity: 1,
					scaling: 1,
					template: null,
					thumbnail: null,
					preview: null,
				},
			);

			// Add a virtual page for the inside of the back cover
			pageModels.push({
				id: COVER_BACK_INSIDE,
				width: offeringModel.width,
				height: offeringModel.height,
				offset: offeringModel.bleedmargin,
				bgcolor: insideCoverBackgroundColor,
				bgimage: insideBackCoverImage,
				bgpattern: coverBackgroundPattern,
				editable: 0,
				movable: 0,
				quantity: 1,
				scaling: 1,
				template: null,
				thumbnail: null,
				preview: null,
			});
		}

		insertBackendOnlyFixedPagesForViewing({
			fixedPageStateModels: ThemeStateModule.getFixedPages,
			offeringModel,
			pageModels,
		});

		if (
			offeringModel
			&& OfferingGroups(
				offeringModel.groupid,
				[
					'PhotoFrameBox',
					'CardGame',
				],
			)
		) {
			let x = 0;

			if (
				offeringModel
				&& OfferingGroups(
					offeringModel.groupid,
					'PhotoFrameBox',
				)
			) {
				x = 1;
			}

			return pageModels.filter((_pageModel, i) => (
				i > x
			));
		}

		return pageModels;
	}

	/**
	 * Getter used to check if the user did not leave the edges of the canvas empty
	 * This is usually by accident (after moving an object) and can lead to returned printed products
	 * This check is used for one-pager products, mostly wall decoration
	 */
	public get getPageMarginCheck() {
		return (pageModel: PI.PageModel): boolean => {
			const pageObjects = this.getPageObjects(pageModel);
			const photoObjects = pageObjects.filter((pageObject) => pageObject.type === 'photo');

			if (photoObjects.length != 1) {
				return true;
			}

			const photoObjectModel = photoObjects[0];
			const objectSurfaceX1 = Math.max(
				0,
				photoObjectModel.x_axis,
			);
			const objectSurfaceX2 = Math.min(
				pageModel.width,
				photoObjectModel.x_axis + photoObjectModel.width,
			);
			const objectSurfaceY1 = Math.max(
				0,
				photoObjectModel.y_axis,
			);
			const objectSurfaceY2 = Math.min(
				pageModel.height,
				photoObjectModel.y_axis + photoObjectModel.height,
			);
			const objectSurfaceWidth = Math.abs(objectSurfaceX2 - objectSurfaceX1);
			const objectSurfaceHeight = Math.abs(objectSurfaceY2 - objectSurfaceY1);
			const objectSurfaceSize = objectSurfaceWidth * objectSurfaceHeight;
			const pageSurfaceSize = pageModel.width * pageModel.height;

			return (
				objectSurfaceSize < (ConfigModule['pageMargin.warningThreshold'] * pageSurfaceSize)
				|| objectSurfaceSize >= pageSurfaceSize
			);
		};
	}

	/**
	 * Getter used to count low-resolution photo objects on the page or in the project.
	 */
	public get getLowQualityPhotoCount() {
		return (pageModel?: PI.PageModel): number => {
			const offeringModel = this.getOffering;

			if (
				shouldApplyUpscaling(
					offeringModel,
					this.getProductSettings,
				)
				|| shouldApplyVectorization(offeringModel)
			) {
				return 0;
			}

			const objectModels = (
				pageModel
					? this.getPageObjects(pageModel)
					: this.getObjects
			);

			return objectModels.reduce(
				(total, pageObjectModel) => {
					if (
						pageObjectModel.type !== 'photo'
						|| !pageObjectModel.photoid
					) {
						return total;
					}

					const photoModel = PhotosModule.getById(pageObjectModel.photoid);
					const photoData = (
						photoModel
							? {
								width: photoModel.full_width,
								height: photoModel.full_height,
							}
							: undefined
					);

					return (
						isLowResolutionPhotoObject({
							objectModel: pageObjectModel,
							offeringModel,
							photoData,
						})
							? total + 1
							: total
					);
				},
				0,
			);
		};
	}

	/**
	 * Getter used to check if one or more objects on the page or in the project will be
	 * printed bigger than the recommended solution
	 */
	public get getPageQuality() {
		return (pageModel?: PI.PageModel): 'high' | 'low' => (
			this.getLowQualityPhotoCount(pageModel) > 0
				? 'low'
				: 'high'
		);
	}

	public get getPagesQuantity() {
		return this.getPages.reduce(
			(memo, pageModel) => memo + pageModel.quantity,
			0,
		);
	}

	/**
	 * Quantity of pages that will be printed, including backend-only fixed pages.
	 */
	public get getPrintedPagesQuantity(): number {
		return getPrintedPagesQuantity(
			this.getPagesQuantity,
			this.getBackendOnlyFixedPagesCount,
		);
	}

	/**
	 * Page-count value used for min/max page validations in overview flows.
	 */
	public get getPageCountForLimitChecks(): number {
		return getPageCountForLimitChecks(
			this.getPages.length,
			this.getBackendOnlyFixedPagesCount,
		);
	}

	public get getPhotosSelected(): PI.PhotoModel[] {
		return this.photoList
			.map((photoId) => PhotosModule.getById(photoId))
			.filter((item): item is PI.PhotoModel => !!item);
	}

	public get getPhotosFailed() {
		return this.getPhotosSelected.filter((photoModel) => !!photoModel._error);
	}

	public get getPhotosQueued(): PI.PhotoModel[] {
		return this.getPhotosSelected.filter(
			(photoModel) => (
				typeof photoModel.id === 'string'
				&& !photoModel._error
			),
		);
	}

	public get getProduct(): DB.ProductModel | null {
		if (this.productId) {
			return ProductsModule.getById(this.productId) || null;
		}

		return null;
	}

	public get getProductId(): DB.ProductModel['id'] | null {
		return this.productId;
	}

	public get getProductSettings(): PI.ProductSettings {
		return this.productSettings;
	}

	public get getProductTour() {
		return (name: DB.ProductTours) => name in this.productTours && this.productTours[name];
	}

	public get getSelectedPageObject(): PI.PageObjectModel | undefined {
		const pageModel = this.getActivePage;

		if (pageModel) {
			const models = this.getPageObjects(pageModel);

			return models.find((model) => model._selected);
		}

		return undefined;
	}

	public get getSelectedPageObjectForEdition(): PI.PageObjectModel | undefined {
		const pageModel = this.getActivePage;

		if (pageModel) {
			const models = this.getPageObjects(pageModel);

			return models.find((model) => model._selectedForEdition);
		}

		return undefined;
	}

	public get getPageTemplate() {
		return (pageModel: PI.PageModel) => {
			const templateId = pageModel.template;

			return (
				templateId
					? ThemeDataModule.getTemplate(templateId)
					: null
			);
		};
	}

	public get getPageTemplateSet(): (options: ProductStateGetPageTemplateSetOptions) => ProductStateTemplateSet {
		return (options) => {
			const {
				pageModel,
				templateSetId,
				photoModels,
			} = options;

			if (!pageModel.template) {
				return {};
			}

			if (
				pageModel.id === SPINE
				&& this.getSpine
			) {
				/**
				 * Add a single text position for the spine text, for this we need
				 * to get the actual spine definition of the page from the
				 * ProductStateModule.getSpine to get the actual width of
				 * it, since the one from the options could be from a viewing spine
				 * with a bigger width just for visualization purposes.
				 */
				const spineModel = this.getSpine;
				const spineTextAngle = 90;
				/**
				 * To avoid the spine text being too close to the edges of the spine
				 * which might cause text to be cut off when printed, we set the width
				 * of the spine text to 80% of its size.
				 */
				const spineTextWidth = spineModel.height * 0.8;
				const spineTextHeight = spineModel.width;
				/**
				 * Offset the spine text placement so the rotated bounding box starts at (0, 0).
				 */
				const spineTextBoundingBox = calculateRotatedBoundingBox({
					height: spineTextHeight,
					rotate: spineTextAngle,
					width: spineTextWidth,
					x_axis: 0,
					y_axis: 0,
				});
				const spineTextOffsetX = -spineTextBoundingBox.x;
				const spineTextOffsetY = -spineTextBoundingBox.y;
				/**
				 * Center the rotated spine text within the visual spine page.
				 */
				const spineViewWidth = (
					pageModel.width
					|| spineTextBoundingBox.width
				);
				const spineViewHeight = (
					pageModel.height
					|| spineTextBoundingBox.height
				);
				const spineCenterOffsetX = (spineViewWidth - spineTextBoundingBox.width) / 2;
				const spineCenterOffsetY = (spineViewHeight - spineTextBoundingBox.height) / 2;
				const fitResult = fit({
					fontFamily: getFontFamily({
						family: FontModule.getDefault.id,
						bold: false,
						italic: false,
					}),
					fontModel: FontModule.getById(FontModule.getDefault.id),
					fontSize: spineModel.width,
					isBold: false,
					maxWidth: spineTextWidth,
					text: '',

					kind: 'font',
					boxHeight: spineTextHeight,
					boxWidth: spineTextWidth,
					minFontSize: MIN_FONTSIZE,
					maxFontSize: pageModel.height,
				});

				return {
					templateSet: {
						id: 'spine-template-set',
						templateId: 'spine-template',
						positions: [
							{
								id: 'spine-text-position',
								x: spineTextOffsetX + spineCenterOffsetX,
								y: spineTextOffsetY + spineCenterOffsetY,
								z: 0,
								width: spineTextWidth,
								height: spineTextHeight,
								angle: spineTextAngle,
								borderwidth: 0,
								bordercolor: '',
								transformable: false,
								available: true,
								overlap_top: 0,
								overlap_left: 0,
								overlap_right: 0,
								overlap_bottom: 0,

								type: 'text',
								fontface: FontModule.getDefault.id,
								pointsize: pixelToPoint(fitResult.usedFontSize),
								fontcolor: '#000000',
								fontbold: false,
								fontitalic: false,
								snap: false,
								bgcolor: null,
								align: 'Center',
								productattribute: null,
								styleable: true,
								attributes: null,
							},
						],
					},
				} satisfies ProductStateTemplateSet;
			}

			const templateModel = ThemeDataModule.getTemplate(pageModel.template);

			if (!templateModel) {
				return {};
			}

			if (templateSetId) {
				const templateSet = ThemeStateModule.getTemplateSetById(
					templateModel.id,
					templateSetId,
					{
						marginAroundEdge: pageModel.templateMarginAroundEdge,
						marginBetweenPositions: pageModel.templateMarginBetweenPositions,
						dpi: options.dpi,
					},
				);

				if (templateSet) {
					return {
						templateSet,
					};
				}
			}

			return ThemeStateModule.getTemplateSet(
				pageModel.template,
				photoModels,
				{
					marginAroundEdge: pageModel.templateMarginAroundEdge,
					marginBetweenPositions: pageModel.templateMarginBetweenPositions,
					dpi: options.dpi,
				},
			);
		};
	}

	public get getPageTemplatePositions(): (options: ProductStateGetPageTemplatePositionsOptions) => TemplateSet['positions'] {
		return (options) => {
			const {
				pageModel,
				photoModels,
			} = options;
			const { templateSet } = this.getPageTemplateSet({
				pageModel,
				templateSetId: (
					pageModel.templateSetId
					?? DEFAULT_DYNAMIC_TEMPLATE_ID
				),
				photoModels: photoModels || this.getPagePhotos(pageModel),
				dpi: options.dpi,
			});

			if (templateSet) {
				return templateSet.positions;
			}

			return [];
		};
	}

	public get getPageTemplatePositionsAvailable(): (options: ProductStateGetPageTemplatePositionsAvailableOptions) => TemplateSet['positions'] {
		return (options) => {
			const {
				pageModel,
				photoModels,
			} = options;
			const positions = this.getPageTemplatePositions({
				pageModel,
				photoModels,
				dpi: options.dpi,
			});
			const objectModels = this.getPageObjects(
				pageModel,
			);

			return positions.filter(
				(positionModel) => TemplatePosition.getAvailability(
					positionModel,
					objectModels,
				),
			);
		};
	}

	public get getSaved(): boolean | null {
		if (!this.productId) {
			return true;
		}

		return this.getPhotosQueued.length + this.getPhotosFailed.length === 0
			? this._saved // _saved can also be 'null'
			: false;
	}

	public get getUnusedPhotos(): PI.PhotoModel[] {
		// Build array of ids from photos already included in product
		const photoIdsUsed: (number | string)[] = [];
		this.getObjects.forEach((objectModel) => {
			if (objectModel.photoid
				&& !photoIdsUsed.includes(objectModel.photoid)
			) {
				photoIdsUsed.push(objectModel.photoid);
			}
		});

		// Build a (cloned) photo collection of all photos linked to this product that have not been included in the product yet
		const newPhotoCollection: PI.PhotoModel[] = [];
		this.getPhotosSelected.forEach((photoModel) => {
			if (!photoIdsUsed.includes(photoModel.id)) {
				newPhotoCollection.push(photoModel);
			}
		});

		return newPhotoCollection;
	}

	public get getThemeId(): DB.ThemeModel['id'] | undefined {
		return this.getProduct?.themeid;
	}

	public get hasFuture(): boolean {
		return this._snapshots.length - 1 > this._historyIndex;
	}

	public get hasHistory(): boolean {
		return this._historyIndex > 0;
	}

	public get willHistoryRestoreChangeOffering(): (type: ProductStateHistoryRestoreType) => boolean {
		return (type) => {
			const historyData = this.getHistoryDataAtIndex(
				this.getHistoryRestoreIndex(type),
			);

			return this.hasProductOfferingChange(
				this.getProduct,
				historyData?.product,
			);
		};
	}

	public get getHistoryDataAtIndex(): (historyIndex: number) => PI.ProductDataModel | undefined {
		return (historyIndex) => {
			const snapshot = this._snapshots[historyIndex];

			if (!snapshot) {
				return undefined;
			}

			return JSON.parse(snapshot) as PI.ProductDataModel;
		};
	}

	public get getHistoryRestoreIndex(): (type: ProductStateHistoryRestoreType) => number {
		return (type) => (
			type === 'undo'
				? this._historyIndex - 1
				: this._historyIndex + 1
		);
	}

	public get hasProductOfferingChange(): (
		previousProduct?: DB.ProductModel | null,
		restoredProduct?: DB.ProductModel | null,
	) => boolean {
		return (
			previousProduct,
			restoredProduct,
		) => !!(
			restoredProduct
			&& (
				previousProduct?.group !== restoredProduct.group
				|| previousProduct?.typeid !== restoredProduct.typeid
				|| previousProduct?.variantid !== restoredProduct.variantid
			)
		);
	}

	public get hasPhoto() {
		return (modelData: Partial<PI.PhotoModel>) => {
			const photoModel = this.findPhoto(modelData);
			return photoModel
				? this.photoList.indexOf(photoModel.id) >= 0
				: false;
		};
	}

	public get hasSavedPhoto() {
		return (id: PI.PhotoModel['id']) => this.photoList.indexOf(id) >= 0;
	}

	@Mutation
	private _addAddress(data: DB.AddressModel): void {
		this.address = reactive(data);
		this._saved = false;
	}

	@Mutation
	private _addAttribute(data: PI.ProductAttributeModel): void {
		this.productAttributes.push(data);
		this._saved = false;
	}

	@Mutation
	private _addObject({
		data,
		pageId,
	}: {
		data: PI.PageObjectModel;
		pageId?: PI.PageModel['id'];
	}): void {
		if (data.id) {
			this.objects[data.id] = data;

			if (
				pageId
				&& this.spine?.id === pageId
			) {
				this.spine?.objectList?.push(data.id);
			} else if (pageId) {
				this.pages[pageId]?.objectList?.push(data.id);
			}

			this._saved = false;
		}
	}

	@Mutation
	private _addPage({
		data,
		at,
	}: {
		data: PI.PageModel;
		at?: number;
	}): void {
		if (data.id && this.pageList.indexOf(data.id) < 0) {
			this.pages[data.id] = data;

			if (typeof at !== 'undefined') {
				this.pageList.splice(
					at,
					0,
					data.id,
				);
			} else {
				this.pageList.push(data.id);
			}

			this._saved = false;
		}
	}

	@Mutation
	private _addSpine(data: PI.PageModel): void {
		this.spine = data;
		this._saved = false;
	}

	@Mutation
	private _removeSpine(): void {
		this.spine = null;
		this._saved = false;
	}

	@Mutation
	public addPhoto({
		photoId,
		index,
	}: {
		photoId: PI.PhotoModel['id'];
		index?: number;
	}): void {
		if (this.photoList.indexOf(photoId) === -1) {
			if (typeof index !== 'undefined') {
				this.photoList.splice(
					index,
					0,
					photoId,
				);
			} else {
				this.photoList.push(photoId);
			}

			this._saved = false;
		}
	}

	@Mutation
	public changeAddress(
		data: Partial<PI.AddressModel>,
	): void {
		if (this.address) {
			if (
				data.country
				&& window.App.router.$i18next.exists(`countries.${data.country}`)
			) {
				data.countryFormatted = window.App.router.$t(`countries.${data.country}`);
			}

			Object.assign(
				this.address,
				data,
			);
		}

		this._saved = false;
	}

	@Mutation
	private _changeHistoryIndex(val: number): void {
		this._historyIndex = val;
	}

	@Mutation
	private _changeLoadingMask(val: boolean): void {
		this._loadingMask = val;
	}

	@Mutation
	private _changeLoadingOfferingFrame(val: boolean): void {
		this._loadingOfferingFrame = val;
	}

	@Mutation
	private _changeLoadingOverlay(val: boolean): void {
		this._loadingOverlay = val;
	}

	@Mutation
	private _changePageObject(data: OptionalExceptFor<PI.PageObjectModel, 'id'>): void {
		if (data.id) {
			this.objects[data.id] = _.extend(
				this.objects[data.id],
				data,
			);

			if (
				_.find(
					_.keys(data),
					(key) => (
						key.charAt(0) !== '_'
						&& key !== 'id'
					),
				)
			) {
				this._saved = false;
			}
		}
	}

	/**
	 * Clears cached promises and version markers for photo loads.
	 */
	@Mutation
	private _clearPageObjectPhotoLoadCaches(): void {
		this._pageObjectPhotoLoadPromises.clear();
		this._pageObjectPhotoLoadVersions.clear();
	}

	/**
	 * Determines if the provided version is still the latest for the given key.
	 *
	 * @param key Load key generated by {@link buildPageObjectPhotoLoadKey}.
	 * @param version Version to validate.
	 * @returns True when the version matches the latest tracked value.
	 */
	private get _isLatestPageObjectPhotoLoadVersion() {
		return (
			key: string,
			version: number,
		): boolean => (
			this._pageObjectPhotoLoadVersions.get(key) === version
		);
	}

	/**
	 * Retrieves the currently tracked version for the provided key.
	 *
	 * @param key Load key generated by {@link buildPageObjectPhotoLoadKey}.
	 * @returns The current version number.
	 */
	private get _getPageObjectPhotoVersion() {
		return (key: string): number => (
			this._pageObjectPhotoLoadVersions.get(key) ?? 0
		);
	}

	/**
	 * Increments the tracked version for the provided key and returns it.
	 *
	 * @param key Load key generated by {@link buildPageObjectPhotoLoadKey}.
	 * @returns The incremented version number.
	 */
	@Mutation
	private _markPageObjectPhotoLoadVersion(key: string): void {
		const nextVersion = (
			this._pageObjectPhotoLoadVersions.get(key)
			?? 0
		) + 1;
		this._pageObjectPhotoLoadVersions.set(
			key,
			nextVersion,
		);
	}

	/**
	 * Removes cached load tracking data for the provided {@link PI.PageObjectModel} identifier.
	 *
	 * @param objectId Identifier whose cache entries should be removed.
	 */
	@Mutation
	private _purgePageObjectPhotoLoadCaches(objectId: PI.PageObjectModel['id']): void {
		const keyPrefix = `${objectId}|`;
		const promiseKeys = Array.from(this._pageObjectPhotoLoadPromises.keys());

		for (const key of promiseKeys) {
			if (key.startsWith(keyPrefix)) {
				this._pageObjectPhotoLoadPromises.delete(key);
			}
		}

		const versionKeys = Array.from(this._pageObjectPhotoLoadVersions.keys());

		for (const key of versionKeys) {
			if (key.startsWith(keyPrefix)) {
				this._pageObjectPhotoLoadVersions.delete(key);
			}
		}
	}

	@Mutation
	public changePage(data: OptionalExceptFor<PI.PageModel, 'id'>): void {
		if (data.id) {
			const persistedPageId = resolvePersistedPageId(
				this,
				data.id,
			);

			if (
				persistedPageId !== data.id
				&& Object.prototype.hasOwnProperty.call(
					this.pages,
					data.id,
				)
			) {
				delete this.pages[data.id];
				this._saved = false;
			}

			if (!persistedPageId) {
				return;
			}

			const normalizedData = (
				persistedPageId === data.id
					? data
					: {
						...data,
						id: persistedPageId,
					}
			);
			const isSpine = (
				this.spine
				&& persistedPageId === this.spine.id
			);
			const currentData = (
				!isSpine
					? this.pages[persistedPageId]
					: this.spine!
			);

			if (!currentData) {
				return;
			}

			// Reset loaded asset when underlying property changes
			if (
				'bgimage' in normalizedData
				&& normalizedData.bgimage !== currentData.bgimage
			) {
				normalizedData._bgimage = null;
			}
			if (
				'bgpattern' in normalizedData
				&& normalizedData.bgpattern !== currentData.bgpattern
			) {
				normalizedData._bgpattern = null;
			}

			if (!isSpine) {
				this.pages[persistedPageId] = _.extend(
					currentData,
					normalizedData,
				);
			} else {
				this.spine = _.extend(
					currentData,
					normalizedData,
				);
			}

			if (
				_.find(
					_.keys(normalizedData),
					(key) => (
						key.charAt(0) !== '_'
						&& key !== 'id'
					),
				)
			) {
				this._saved = false;
			}
		}
	}

	@Mutation
	public _changePhotoId([
		oldId,
		newId]: [PI.PhotoModel['id'], PI.PhotoModel['id'],
	]): void {
		// Replace in history
		this._snapshots.forEach((snapshot, si) => {
			const historicData: PI.ProductDataModel = JSON.parse(
				snapshot,
			);

			// Replace entry in photoList
			const pi = _.indexOf(
				historicData.photoList,
				oldId,
			);

			if (pi !== -1) {
				historicData.photoList[pi] = newId;
			}

			// Replace entry in objects
			if (historicData.objects) {
				const objectModels = _.toArray(historicData.objects);
				objectModels.forEach((objectModel) => {
					if (objectModel.photoid && objectModel.photoid == oldId) {
						objectModel.photoid = newId;
					}
				});
			}

			// Replace snapshot entry
			this._snapshots[si] = JSON.stringify(historicData);
		});

		// Replace in current data
		const i = this.photoList.findIndex(
			(id) => id === oldId,
		);

		if (i !== -1) {
			this.photoList[i] = newId;
			this._saved = false;
		}
	}

	@Mutation
	public changeProductAttribute({ data }: {
		data: RequiredExceptFor<PI.ProductAttributeModel, 'id'>;
	}): void {
		const i = _.findIndex(
			this.productAttributes,
			{
				property: data.property,
			},
		);

		if (i != -1) {
			this.productAttributes[i].value = data.value;
		}
	}

	/**
	 * Applies project-level background settings to every eligible persisted page
	 * within one Vuex mutation. Pages using a transparent background image retain
	 * their configured background, while the spine always follows project settings.
	 *
	 * @param data Background properties shared by the project pages.
	 */
	@Mutation
	private _changePageBackgrounds(data: PageBackgroundChange): void {
		for (const pageId of this.pageList) {
			const pageModel = this.pages[pageId];

			if (
				!pageModel?.id
				|| (
					pageModel.bgcolor === 'transparent'
					&& pageModel.bgimage
				)
			) {
				continue;
			}

			this.pages[pageId] = _.extend(
				pageModel,
				data,
			);
		}

		if (this.spine?.id) {
			this.spine = _.extend(
				this.spine,
				data,
			);
		}
	}

	@Mutation
	private _changeProductSettings(data: Partial<PI.ProductSettings>): void {
		Object.assign(
			this.productSettings,
			data,
		);
		this._saved = false;
	}

	@Mutation
	public disableCropping(pageId: PI.PageModel['id']): void {
		const pageModel = this.pages[pageId];

		if (pageModel && pageModel.objectList) {
			const models = _.compact(pageModel.objectList.map((objectId) => this.objects[objectId]));
			models.forEach(
				(objectModel) => {
					objectModel._crop = false;
				},
			);
		}
	}

	@Mutation
	public mergeCustomAttributes(data: Record<string, string>): void {
		this.customAttributes = reactive({
			...this.customAttributes,
			...data,
		});
		this._saved = false;
	}

	@Mutation
	public movePage({
		from,
		to,
	}: {
		from: number;
		to: number;
	}): void {
		this.pageList.splice(
			to,
			0,
			this.pageList.splice(
				from,
				1,
			)[0],
		);
		this._saved = false;
	}

	@Mutation
	private _popHistory(): void {
		this._snapshots.pop();
		this._historyIndex -= 1;
	}

	@Mutation
	private _pushHistory(data: object): void {
		/*
		 * We are starting a new timeline
		 * Remove any forward history from the snapshots
		 */
		while (this._snapshots.length - 1 > this._historyIndex) {
			this._snapshots.pop();
		}

		this._snapshots.push(JSON.stringify(data));
		this._historyIndex += 1;
	}

	@Mutation
	private _removeAddress(): void {
		this.address = null;
		this._saved = false;
	}

	@Mutation
	private _removeAttribute(attributeId: string): void {
		const i = _.findIndex(
			this.productAttributes,
			(attributeModel) => attributeModel.id == attributeId,
		);

		if (i !== -1) {
			this.productAttributes.splice(
				i,
				1,
			);
		}

		this._saved = false;
	}

	@Mutation
	private _removePage(id: PI.PageModel['id']): void {
		const persistedPageId = resolvePersistedPageId(
			this,
			id,
		);

		if (
			persistedPageId !== id
			&& Object.prototype.hasOwnProperty.call(
				this.pages,
				id,
			)
		) {
			delete this.pages[id];
		}

		if (!persistedPageId) {
			this._saved = false;
			return;
		}

		this.pageList = _.without(
			this.pageList,
			persistedPageId,
		);
		delete this.pages[persistedPageId];

		this._saved = false;
	}

	@Mutation
	private _removePageObject(id: PI.PageObjectModel['id']): void {
		Object.values(this.pages).forEach((pageModel) => {
			if (pageModel?.objectList) {
				const objectIndex = pageModel.objectList.indexOf(id);

				if (objectIndex !== -1) {
					pageModel.objectList.splice(
						objectIndex,
						1,
					);
				}
			}
		});

		if (this.spine?.objectList) {
			const objectIndex = this.spine.objectList.indexOf(id);

			if (objectIndex !== -1) {
				this.spine.objectList.splice(
					objectIndex,
					1,
				);
			}
		}

		delete this.objects[id];

		this._saved = false;
	}

	@Action({
		rawError: true,
	})
	public removePageObject(id: PI.PageObjectModel['id']): void {
		this._purgePageObjectPhotoLoadCaches(id);
		this._removePageObject(id);
	}

	@Mutation
	private _removePhoto(photoid: PI.PhotoModel['id']): void {
		const i = _.indexOf(
			this.photoList,
			photoid,
		);

		if (i !== -1) {
			this.photoList.splice(
				i,
				1,
			);
		}

		this._saved = false;
	}

	@Mutation
	private _resetToDefaults(): void {
		// Reset data to defaults
		Object.assign(
			this,
			JSON.parse(JSON.stringify(defaults.productStateModel)),
		);
		this._thinLineWarningPageIds.splice(0);
	}

	@Action({
		rawError: true,
	})
	private _reset(): void {
		this._resetToDefaults();
		this._clearPageObjectPhotoLoadCaches();
		this._setSaved(true);
	}

	@Mutation
	public resetActivePage(): void {
		this._activePage = null;
	}

	@Mutation
	private _resetHistory(): void {
		this._snapshots = [];
		this._historyIndex = -1;
	}

	@Mutation
	private _resetObjects(): void {
		Object.keys(this.objects).forEach((objectId) => {
			delete this.objects[objectId];
		});
	}

	@Mutation
	public resetPageList(pageIds: PI.PageModel['id'][]): void {
		this.pageList = pageIds || [];
		this._saved = false;
	}

	@Mutation
	public _resetPages(): void {
		Object.keys(this.pages).forEach((pageId) => {
			delete this.pages[pageId];
		});
	}

	@Mutation
	public resetPhotos(photoIds: (PI.PhotoModel['id'])[]): void {
		this.photoList = photoIds || [];
		this._saved = false;
	}

	@Mutation
	public setActivePage(pageId: PI.PageModel['id']): void {
		if (this.pages[pageId]) {
			this._activePage = pageId;
		} else if (pageId === this.getSpineForViewing?.id) {
			this._activePage = pageId;
		} else {
			this._activePage = null;
		}
	}

	@Mutation
	public setBookmarks(data: PI.BookmarksModel): void {
		Object.assign(
			this._bookmarks,
			data,
		);
	}

	@Mutation
	public setCustomAttributes(data: Record<string, string>): void {
		this.customAttributes = reactive(data);
		this._saved = false;
	}

	@Mutation
	private _setMetaData(data: PI.ProductDataModel['metaData']): void {
		if (data) {
			this.metaData = merge(
				this.metaData,
				data,
			);
			this._saved = false;
		}
	}

	@Mutation
	private _setMaskImage(img: HTMLImageElement | null): void {
		if (img !== null) {
			this._mask = reactive(img);
		} else {
			this._mask = img;
		}
	}

	@Mutation
	private _setOverlayImage(img: HTMLImageElement | null): void {
		if (img !== null) {
			this._overlay = reactive(img);
		} else {
			this._overlay = img;
		}
	}

	@Mutation
	private _setProductId(id: number): void {
		this.productId = id;
	}

	@Mutation
	private _setProductTour(data: PI.ProductDataModel['productTours']): void {
		Object.assign(
			this.productTours,
			data,
		);
		this._saved = false;
	}

	@Mutation
	private _setThinLineDetected(value?: boolean): void {
		const nextValue = value === true;

		if (this.thinLineDetected === nextValue) {
			return;
		}

		this.thinLineDetected = nextValue;
		this._saved = false;
	}

	@Mutation
	private _setThinLineWarningPageIds(pageIds: PI.PageModel['id'][]): void {
		const nextPageIds = Array.from(new Set(pageIds));
		const isUnchanged = (
			nextPageIds.length === this._thinLineWarningPageIds.length
			&& nextPageIds.every((pageId, index) => pageId === this._thinLineWarningPageIds[index])
		);

		if (isUnchanged) {
			return;
		}

		this._thinLineWarningPageIds.splice(
			0,
			this._thinLineWarningPageIds.length,
			...nextPageIds,
		);
	}

	@Mutation
	private _setSaved(isSaved: boolean | null): void {
		this._saved = isSaved;
	}

	@Mutation
	private _setVersion(timestamp?: number): void {
		this.version = timestamp || Math.floor(Date.now() / 1000);
	}

	@Mutation
	private _shiftHistory(): void {
		this._snapshots.shift();
		this._historyIndex -= 1;
	}

	/**
	 * Synchronizes theme and mask state with the product restored from a history snapshot.
	 *
	 * @param previousProduct Product data prior to applying the history snapshot
	 * @param restoredProduct Product data retrieved from the history snapshot
	 * @returns Promise that resolves once dependent state is aligned with the restored product
	 */
	@Action
	private async _syncThemeAndMaskForHistoryChange(
		options: {
			previousProduct: DB.ProductModel | null;
			restoredProduct: DB.ProductModel | null;
		},
	): Promise<void> {
		const {
			previousProduct,
			restoredProduct,
		} = options;

		if (!restoredProduct) {
			return undefined;
		}

		const offeringChanged = (
			previousProduct?.group !== restoredProduct.group
			|| previousProduct?.typeid !== restoredProduct.typeid
			|| previousProduct?.variantid !== restoredProduct.variantid
		);
		const themeChanged = previousProduct?.themeid !== restoredProduct.themeid;

		if (
			(
				themeChanged
				|| offeringChanged
			)
			&& restoredProduct.themeid
		) {
			try {
				await Theme.setup(restoredProduct.themeid);
			} catch (error: any) {
				/**
				 * Theme fetch failed, check if we are still online, or that
				 * offline status has been detected during the fetch
				 */
				if (!AppStateModule.online) {
					/**
					 * Indicate the fetch failed due to offline status
					 */
					throw new Error(ERRORS_OFFLINE);
				}

				reportError(
					new Error(`Could not fetch theme data for theme ${restoredProduct.themeid}`),
					'error',
				);

				/**
				 * Still online, so there must be something wrong with the
				 * data fetch, in which case offer error dialog to let user
				 * try again
				 */
				return new Promise((resolve, reject) => {
					let dialogErrorText = window.App.router.$t('dialogTextErrorThemeFetch');

					if (error.message) {
						dialogErrorText += `\n\nError: ${error.message}`;
					}

					const closeError = DialogService.openErrorDialog({
						body: {
							content: dialogErrorText,
						},
						footer: {
							buttons: [
								{
									id: 'abort',
									text: window.App.router.$t('dialogButtonErrorAbort'),
									click: () => {
										closeError();
										reject(error);
									},
								},
								{
									id: 'accept',
									text: window.App.router.$t('dialogButtonErrorRetry'),
									click: () => {
										closeError();
										this._syncThemeAndMaskForHistoryChange({
											previousProduct,
											restoredProduct,
										})
											.then(
												resolve,
												reject,
											);
									},
								},
							],
						},
					});
				});
			}
		}

		if (offeringChanged) {
			/**
			 * Reset mask assets to ensure the restored offering uses the matching overlay.
			 */
			this.resetMaskImage();
			AppStateModule.setMask(null);
		}

		return undefined;
	}

	/**
	 * Replaces the product state from persisted API data while retaining each object's rendering version.
	 *
	 * @param data Persisted {@link API.ProjectDataModel} to load into the product store.
	 * @returns The loaded {@link DB.ProductModel} after its related project data has been parsed.
	 */
	@Action({
		rawError: true,
	})
	public async _parseData({ data }: {
		data: API.ProjectDataModel;
	}): Promise<DB.ProductModel> {
		// Disable auto saving while data is being replaced (to avoid sending incomplete data model)
		AppStateModule.disableAutoSave();
		this._reset();

		if (!data) {
			return Promise.reject(
				new Error(ERRORS_INVALID_REQUEST_DATA),
			);
		}

		ProductsModule.addModel(data.product);
		this._setProductId(
			data.product.id,
		);

		if (data.version) {
			this._setVersion(
				data.version,
			);
		}

		if (data.metaData) {
			this._setMetaData(
				data.metaData,
			);
		}

		this._setThinLineDetected(data.thinLineDetected);

		if (data.photos) {
			await PhotosModule.addModels(_.toArray(data.photos) as PI.PhotoModel[]);
		}

		const arrObjects = _.toArray(
			data.objects,
		);

		let objectsPromise: Promise<void> = Promise.resolve();

		if (arrObjects.length > 0) {
			arrObjects.forEach(
				(objectData) => {
					objectsPromise = objectsPromise.then(
						() => this.addPageObject({
							data: objectData,
							preserveVersion: true,
						}).then(() => undefined),
					);
				},
			);
		}

		return objectsPromise
			.then(() => {
				if (data.pages) {
					data.pageList.map(
						(pageId) => data.pages[pageId],
					).forEach(
						(pageData, i) => {
							this.addPage(
								{
									data: pageData,
									at: i,
								},
							);
						},
					);
				}

				if (data.address) {
					this.addAddress(
						{
							data: data.address,
						},
					);
				}

				if (data.photoList) {
					this.addPhotos(
						data.photoList,
					);
				}

				if (data.customAttributes) {
					this.setCustomAttributes(
						data.customAttributes,
					);
				}

				if (data.productAttributes) {
					data.productAttributes.forEach(
						(attributeData) => {
							this.addAttribute(
								{
									data: attributeData,
								},
							);
						},
					);
				}

				if (data.productSettings) {
					this._changeProductSettings(
						data.productSettings,
					);
				}

				if (data.productTours) {
					this._setProductTour(
						data.productTours,
					);
				}

				if (data.spine) {
					this.addSpine(data.spine);
				}

				this._setSaved(
					true,
				);

				AppStateModule.enableAutoSave();

				this.pushHistory();

				return data.product;
			})
			.then(async (product) => {
				if (product) {
					const searchProps: Partial<DB.OfferingModel> = {
						groupid: product.group,
						typeid: product.typeid,
					};

					if (product.variantid) {
						searchProps.variantid = product.variantid;
					}

					const offeringModelFound = AppDataModule.findOfferingWhere(searchProps);

					if (!offeringModelFound) {
						await AppDataModule.fetchOfferingsData({
							searchProps: {
								...searchProps,
								/**
								 * Indicates to base the fetch of the offering data on the `flexgroupid`
								 * from the offering model found from the result of the `searchProps`
								 */
								get_from_flexgroupid: true,
							},
						});
					}

					/**
					 * There are some edge scenarios where a race conditions happens making the offering
					 * model to be available at this point when the `ProductStateModule.fetch()` called
					 * `AppDataModule.getOffering(offeringId)`, when this happens the condition from above
					 * doesn't get filled causing the same flexgroup offering models to not be searched to,
					 * in that case we create a fallback to check if the amount of offerings available for
					 * that flexgroup are the same loaded in the store, if not then we fetch them from
					 * the found offering model's id
					 */
					if (offeringModelFound) {
						const offeringModelsSameFlexGroup = (
							!!offeringModelFound.flexgroupid
							&& AppDataModule.findOffering({
								flexgroupid: offeringModelFound.flexgroupid,
							})
						);
						const flexGroupModel = (
							!!offeringModelFound.flexgroupid
							&& AppDataModule.flexgroups.find((flexGroup) => flexGroup.id === offeringModelFound.flexgroupid)
						);

						if (
							flexGroupModel
							&& offeringModelsSameFlexGroup
							&& flexGroupModel.offeringscount !== offeringModelsSameFlexGroup.length
						) {
							await AppDataModule.fetchOfferingsData({
								searchProps: {
									id: offeringModelFound.id,
									get_from_flexgroupid: true,
								},
							});
						}
					}
				}

				return ProductStateClass.select(
					data.product.id,
				);
			})
			.catch((err) => {
				this._reset();
				AppStateModule.enableAutoSave();

				return Promise.reject(err);
			});
	}

	@Action({
		rawError: true,
	})
	public acceptPageQuality(
		pageId: PI.PageModel['id'],
	): void {
		if (!pageId) {
			throw new Error(ERRORS_INVALID_REQUEST_DATA);
		}

		this.changePage(
			{
				id: pageId,
				_acceptedQualityWarning: true,
			},
		);
	}

	@Action({
		rawError: true,
	})
	public addAddress({ data }: {
		data: DB.AddressModel;
	} = {
		data: JSON.parse(JSON.stringify(defaults.addressModel)),
	}): Promise<DB.AddressModel> {
		const commitData: DB.AddressModel = {
			...JSON.parse(JSON.stringify(defaults.addressModel)),
			...data,
		};

		if (!commitData.country) {
			// Get default shipping country
			const countryId = this.context.rootState.user.countryid;
			const countryModel = this.context.rootGetters['appdata/getCountry'](countryId);
			commitData.country = countryModel.iso;
		}

		this._addAddress(
			commitData,
		);

		const addressModel = this.getAddress;

		if (!addressModel) {
			return Promise.reject(
				new Error('Something went wrong creating the address model'),
			);
		}

		return Promise.resolve(addressModel);
	}

	@Action({
		rawError: true,
	})
	public addAttribute({ data }: {
		data: RequiredExceptFor<PI.ProductAttributeModel, 'id'>;
	}): PI.ProductAttributeModel | undefined {
		if (data) {
			this._addAttribute({
				...data,
				id: data.id || nanoid(),
			});

			return _.findWhere(
				this.productAttributes,
				{
					id: data.id,
				},
			);
		}

		return undefined;
	}

	@Action({
		rawError: true,
	})
	public addPage({
		data,
		at,
	}: {
		data: Partial<PI.PageModel>;
		at: number;
	}): Promise<PI.PageModel> {
		if (
			!data.offeringId
			&& this.getOffering?.virtual
		) {
			data.offeringId = this.getChildOfferingId;
		}

		const pageData = {
			...JSON.parse(JSON.stringify(defaults.pageModel)),
			...data,
			id: (
				data.id
				|| nanoid()
			),
		};

		this._addPage({
			data: pageData,
			at,
		});

		const pageModel = this.getPage(pageData.id);

		if (!pageModel) {
			throw new Error('Something went wrong creating the page model');
		}

		return Promise.resolve(pageModel);
	}

	@Action({
		rawError: true,
	})
	public addSpine(data: Partial<PI.PageModel>): Promise<PI.PageModel> {
		const spineData: PI.PageModel = {
			...JSON.parse(JSON.stringify(defaults.spineModel)),
			...data,
			id: (
				data.id
				|| nanoid()
			),
			orientation: defaults.spineModel.orientation,
			movable: defaults.spineModel.movable,
		};
		this._addSpine(spineData);

		const spineModel = this.getPage(spineData.id);

		if (!spineModel) {
			throw new Error('Something went wrong creating the spine model');
		}

		return Promise.resolve(spineModel);
	}

	@Action({
		rawError: true,
	})
	public removeSpine(): Promise<void> {
		if (this.spine) {
			const objectModels: PI.PageObjectModel[] = this.getPageObjects(this.spine);

			for (const objectModel of objectModels) {
				this.removePageObject(objectModel.id);
			}

			this._removeSpine();
		}

		return Promise.resolve();
	}

	@Action({
		rawError: true,
	})
	public addObject({
		data,
		pageId,
	}: AddObjectModel): Promise<PI.PageObjectModel> {
		this._addObject({
			data,
			pageId,
		});
		const objectModel = this.getPageObject(data.id);

		if (objectModel) {
			return Promise.resolve(objectModel);
		}

		return Promise.reject(new Error('Something went wrong adding the model'));
	}

	/**
	 * Adds a page object to the product store, generating missing SVG markup for current-format text with content.
	 *
	 * @param options Store-backed {@link AddPageObjectModelWithoutNoStore} insertion options.
	 * @returns The inserted {@link PI.PageObjectModel}.
	 */
	public async addPageObject(options: AddPageObjectModelWithoutNoStore): Promise<PI.PageObjectModel>;

	/**
	 * Adds a page object to caller-owned page data, generating missing SVG markup for current-format text with content.
	 *
	 * @param options Storeless {@link AddPageObjectModelWithNoStore} insertion options.
	 * @returns The inserted {@link PI.PageObjectModel}.
	 */
	public async addPageObject(options: AddPageObjectModelWithNoStore): Promise<PI.PageObjectModel>;

	/**
	 * Normalizes and inserts a page object while ensuring text with content has its current SVG representation.
	 *
	 * @param options Store-backed or storeless {@link AddPageObjectModel} insertion options.
	 * @returns The normalized and inserted {@link PI.PageObjectModel}.
	 */
	@Action({
		rawError: true,
	})
	public async addPageObject(options: AddPageObjectModel): Promise<PI.PageObjectModel> {
		const { data } = options;

		if (
			!data
			|| !data.type
		) {
			return Promise.reject(new Error(ERRORS_INVALID_REQUEST_DATA));
		}

		if (
			!('id' in data)
			&& !options.preserveVersion
		) {
			data.version = '12.30.0';
		}

		const pointSizeFontSize = (
			'pointsize' in data
			&& data.pointsize
			&& !data.fontsize
				? getTextObjectFontSize({
					pointsize: data.pointsize,
				})
				: undefined
		);

		if (
			typeof pointSizeFontSize === 'number'
			&& defaults.objectModel.fontsize
			&& pointSizeFontSize !== defaults.objectModel.fontsize
		) {
			data.fontsize = pointSizeFontSize;
		}

		const noStore = !!options.noStore;
		const newObjectData: PI.PageObjectModel = {
			...JSON.parse(JSON.stringify(defaults.objectModel)),
			...data,
		};
		const photoModel = (
			newObjectData.photoid
				? PhotosModule.getById(newObjectData.photoid)
				: undefined
		);
		const isVectorSVG = (
			(
				photoModel?._type
				&& MIME_TYPES_SVG.includes(photoModel._type)
			)
			|| (
				photoModel?.url
				&& photoModel.url.indexOf('.svg') > 0
			)
			|| photoModel?._vectorize
		);
		const hasExplicitExtension = (
			typeof data.ext === 'string'
			&& data.ext.length > 0
		);
		const shouldUseVectorExtension = (
			typeof options.useRaster === 'undefined'
				? !hasExplicitExtension
				: !options.useRaster
		);

		if (newObjectData.fontface) {
			const fontModel = FontModule.getById(newObjectData.fontface);

			if (
				fontModel
				&& !fontModel._loaded
			) {
				return FontModule
					.loadModel(fontModel.id)
					.then(() => this.addPageObject(options as AddPageObjectModelWithoutNoStore & AddPageObjectModelWithNoStore));
			}
		}

		if (
			'effect' in newObjectData
			&& newObjectData.effect === 'noeffect'
		) {
			// Convert old default property value
			newObjectData.effect = null;
		}

		if (
			typeof options.useRaster === 'boolean'
			&& !options.useRaster
			&& isVectorSVG
		) {
			newObjectData.ext = 'svg';
		} else if (newObjectData.photoid) {
			if (
				/**
				 * The `window.glPlatform` check is needed because the `render-page` lambda function is
				 * sending the entire object list of the project but only the photo models for the page
				 * being rendered, so when adding the page objects to the store there are objects with
				 * no photo model available, so we need to skip the check in this case.
				 * In the future, the `render-page` lambda function should only send the objects for the
				 * page being rendered and this check can be removed.
				 */
				window.glPlatform !== 'server'
				&& !photoModel
			) {
				return Promise.reject(new Error(ERRORS_INVALID_REQUEST_DATA));
			}

			if (
				(
					photoModel?._type
					&& MIME_TYPES_SHOWN_AS_PNG.includes(photoModel._type)
				)
				|| (
					photoModel?.url
					&& urlTools.parse(photoModel.url).extension === 'png'
				)
				|| (
					isVectorSVG
					&& typeof options.useRaster === 'boolean'
					&& options.useRaster
					&& photoModel?.full_url
					&& urlTools.parse(photoModel.full_url).extension === 'png'
				)
			) {
				newObjectData.ext = 'png';
			}

			if (
				isVectorSVG
				&& shouldUseVectorExtension
			) {
				newObjectData.ext = 'svg';
			}
		}

		if (newObjectData.mask === 'square') {
			newObjectData.mask = null;
		}

		/**
		 * Generate the current SVG text representation whenever current-format text is
		 * present but no SVG data exists. This covers current persisted objects and
		 * new insertions such as editable text copied from a theme, while project
		 * clones can preserve legacy rendering through their source version.
		 */
		if (
			newObjectData.type === 'text'
			&& newObjectData.text
			&& !newObjectData.text_svg
			&& typeof newObjectData.version === 'string'
			&& newObjectData.version !== 'legacy'
		) {
			newObjectData.text_svg = createTextSVGString(newObjectData);
		}

		const newObjectId = (
			newObjectData.id
			|| nanoid()
		);
		let objectModel: PI.PageObjectModel | undefined;

		if (!noStore) {
			const pageId = (
				(
					'pageId' in options
					&& options.pageId
				)
				|| undefined
			);
			this._addObject({
				data: {
					...newObjectData,
					id: newObjectId,
				},
				pageId,
			});
			objectModel = this.getPageObject(newObjectId);
		} else if ('pageModel' in options) {
			objectModel = {
				...newObjectData,
				id: newObjectId,
			};
			options.pageObjects.push(objectModel);
			options.pageModel.objectList?.push(newObjectId);
		}

		if (objectModel) {
			return Promise.resolve(objectModel);
		}

		return Promise.reject(new Error('Something went wrong creating the model'));
	}

	@Action({
		rawError: true,
	})
	public addPhotos(arrData: (PI.PhotoModel['id'])[]): void {
		arrData.forEach(
			(photoId) => {
				this.addPhoto({
					photoId,
				});
			},
		);
	}

	@Action({
		rawError: true,
	})
	private _autoFixData(): Promise<void> {
		// Get product data
		const data: PI.ProductDataModel = this.getData;

		Object.keys(data.objects).forEach((objectId) => {
			const objectModel = data.objects[objectId];

			if (objectModel.type == 'photo') {
				const storeObjectModel = this.objects[objectId];

				if (
					storeObjectModel
					&& hasShapeMask(storeObjectModel.mask)
					&& (
						!!storeObjectModel.borderimage
						|| !!storeObjectModel.borderwidth
						|| !!storeObjectModel._borderimage
					)
				) {
					this._changePageObject({
						id: objectModel.id,
						borderimage: 0,
						borderwidth: 0,
						_borderimage: null,
					});
				}

				if (objectModel.photoid) {
					if (data.photoList.indexOf(objectModel.photoid) === -1) {
						// Photo used in photo object, so should be linked to product
						this.addPhoto({
							photoId: objectModel.photoid,
						});
					}
				} else if (!objectModel.source) {
					// Corrupt photo object, remove from data
					this.removePageObject(objectModel.id);
				}
			}
		});

		return Promise.resolve();
	}

	public async changePageObject(data: ChangePageObjectModelWithNoStore): Promise<PageObjectModelChangeResult | undefined>;

	public async changePageObject(data: ChangePageObjectModelWithIgnorePointerSizeResize): Promise<PageObjectModelChangeResult | undefined>;

	@Action({
		rawError: true,
	})
	public async changePageObject(data: ChangePageObjectModel): Promise<PageObjectModelChangeResult | undefined> {
		const noStore = (
			'noStore' in data
			&& !!data.noStore
		);
		/**
		 * Clone data to avoid mutating the original object
		 */
		data = {
			...data,
		};

		// Reset loaded asset when underlying property changes
		if ('borderimage' in data) {
			data._borderimage = null;
		}
		if (
			'source' in data
			|| 'photoid' in data
			|| 'effect' in data
		) {
			data._image = null;
			data._canvas = null;
		}
		if ('mask' in data) {
			data._mask = null;
			data._canvas = null;
		}

		let objectModel: PI.PageObjectModel | null = null;

		if (
			data.id
			&& !noStore
		) {
			objectModel = this.getPageObject(data.id);
		} else if (
			data.id
			&& 'noStore' in data
			&& 'originalPageObject' in data
			&& noStore
			&& data.originalPageObject
		) {
			objectModel = data.originalPageObject;
		}

		if (!objectModel) {
			// Object no longer exists, ignore request
			return undefined;
		}

		const maskAfterChange = (
			'mask' in data
				? data.mask
				: objectModel.mask
		);

		if (
			objectModel.type === 'photo'
			&& hasShapeMask(maskAfterChange)
			&& (
				!!objectModel.borderimage
				|| !!objectModel.borderwidth
				|| !!data.borderimage
				|| !!data.borderwidth
			)
		) {
			data.borderimage = 0;
			data.borderwidth = 0;
			data._borderimage = null;
		}

		const pageObject: PI.PageObjectModel = {
			...objectModel,
		};

		if ('fontface' in data) {
			if (typeof pageObject.text_formatted !== 'undefined') {
				data.text_formatted = undefined;
			}
			if (typeof pageObject.text_formatted_for_canvas !== 'undefined') {
				data.text_formatted_for_canvas = undefined;
			}

			data.text_svg = '';
		}

		if (
			(
				'cropx' in data
				|| 'cropy' in data
			)
			&& pageObject.mask
		) {
			// The canvas needs to be repainted after changing the photo cropping
			data._canvas = null;
		}

		const partialPageObject: OptionalExceptFor<PI.PageObjectModel, 'id'> = {
			id: pageObject.id,
		};
		const finalUpdatedProperties = Object.keys(data) as Array<keyof ChangePageObjectModelWithNoStore>;

		if (finalUpdatedProperties.includes('id')) {
			finalUpdatedProperties.splice(
				finalUpdatedProperties.indexOf('id'),
				1,
			);
		}

		for (const finalUpdatedProperty of finalUpdatedProperties) {
			if (
				finalUpdatedProperty !== 'ignoreFontSizeResize'
				&& finalUpdatedProperty !== 'noStore'
				&& finalUpdatedProperty !== 'originalPageObject'
			) {
				(pageObject as any)[finalUpdatedProperty] = data[finalUpdatedProperty];
				partialPageObject[finalUpdatedProperty] = data[finalUpdatedProperty];
			}
		}

		/**
		 * In case the text object has svg content defined, we need to recreate the svg text
		 * and also signal the image to be reset but ONLY anything related to the rendering
		 * of the text object changed.
		 * If either the x or y axis is changed, then this means that the text object is only
		 * changing its position, so we don't need to recreate the svg text neither reset the image.
		 */
		if (
			pageObject.type === 'text'
			&& typeof pageObject.text !== 'undefined'
			&& (
				'fontbold' in partialPageObject
				|| 'fontface' in partialPageObject
				|| 'fontitalic' in partialPageObject
				|| 'fontunderline' in partialPageObject
				|| 'fontcolor' in partialPageObject
				|| 'fontsize' in partialPageObject
				|| 'pointsize' in partialPageObject
				|| 'text' in partialPageObject
				|| 'bgcolor' in partialPageObject
				|| 'align' in partialPageObject
				|| 'width' in partialPageObject
				|| 'height' in partialPageObject
				|| 'maxwidth' in partialPageObject
				|| 'maxheight' in partialPageObject
				|| 'cropx' in partialPageObject
				|| 'cropy' in partialPageObject
				|| 'cropwidth' in partialPageObject
				|| 'cropheight' in partialPageObject
			)
		) {
			partialPageObject.text_svg = createTextSVGString(pageObject);
			partialPageObject._image = null;

			if (
				pageObject.text_formatted
				|| pageObject.text_formatted_for_canvas
			) {
				partialPageObject.text_formatted = undefined;
				partialPageObject.text_formatted_for_canvas = undefined;
			}

			if (
				pageObject.pointsize
				&& partialPageObject.fontsize
			) {
				partialPageObject.pointsize = undefined;
			}

			if (pageObject.version === 'legacy') {
				partialPageObject.version = '12.30.0';
			}
		}

		if (!noStore) {
			this._changePageObject(partialPageObject);
		}

		return {
			partialPageObject,
			updatedPageObject: pageObject,
		};
	}

	public async loadPageObjectPhoto(options: LoadPageObjectPhotoOptionsWithNoStore): Promise<PI.PageObjectModel>;

	public async loadPageObjectPhoto(options: LoadPageObjectPhotoOptionsWithStore): Promise<PI.PageObjectModel>;

	@Action({
		rawError: true,
	})
	public async loadPageObjectPhoto(options: LoadPageObjectPhotoOptions): Promise<PI.PageObjectModel> {
		const addPrintEffect = (
			options.addPrintEffect
			?? false
		);
		const forceReload = (
			options.forceReload
			?? false
		);
		const forceRemote = (
			options.forceRemote
			?? false
		);
		const includeMask = (
			options.includeMask
			?? true
		);
		const offeringModel = (
			options.offeringModel
			?? this.getOffering
			?? null
		);
		const {
			printEffectColor,
			resolution = 'low',
			scaling,
		} = options;
		const noStore = (
			'noStore' in options
			&& !!options.noStore
		);

		const objectModel = (
			noStore
				? options.originalPageObject
				: this.getPageObject(options.id)
		);

		/**
		 * TODO: the following comment has been made by Copilot, ideally to be addressed in the future:
		 *
		 * [nitpick] The load key generation creates a string by joining multiple parameters.
		 * For frequently called operations, consider using a more efficient key generation strategy
		 * such as hashing or using a WeakMap with object references to improve performance.
		 */
		const loadKey = buildPageObjectPhotoLoadKey(
			objectModel.id,
			{
				addPrintEffect,
				forceRemote,
				includeMask,
				offeringModelId: (
					offeringModel?.id
					?? null
				),
				printEffectColor,
				resolution,
				scaling,
			},
		);

		if (
			!forceReload
			&& this._pageObjectPhotoLoadPromises.has(loadKey)
		) {
			return this._pageObjectPhotoLoadPromises.get(loadKey)!;
		}

		if (forceReload) {
			this._pageObjectPhotoLoadPromises.delete(loadKey);
		}

		this._markPageObjectPhotoLoadVersion(loadKey);
		const loadVersion = this._getPageObjectPhotoVersion(loadKey);

		if (noStore) {
			objectModel._loading = true;
		} else {
			this._changePageObject({
				id: objectModel.id,
				_loading: true,
			});
		}

		const workingCopy: PI.PageObjectModel = {
			...objectModel,
			_loading: false,
		};

		const basePromise = loadPhotoObject(
			workingCopy,
			{
				forceRemote,
				mask: includeMask,
				no_store: true,
				offeringModel,
				resolution,
				scaling,
			},
		)
			.then(async (updatedObjectModel) => {
				if (
					updatedObjectModel.ext === 'svg'
					&& updatedObjectModel._vectorSVG
					&& updatedObjectModel._image
					&& updatedObjectModel._vectorColors
				) {
					const { _vectorColors } = updatedObjectModel;
					const colorReplacement: SVGColorReplacement[] = [];

					if (
						addPrintEffect
						&& printEffectColor
					) {
						const backgroundReplacement = (
							_vectorColors.background
							&& updatedObjectModel.colorReplacement?.find((replacement) => replacement.color === _vectorColors.background?.color)
						);

						if (backgroundReplacement) {
							colorReplacement.push({
								color: backgroundReplacement.color,
								replace: (
									backgroundReplacement.replace.visual
									|| backgroundReplacement.replace.real
								),
							});
						} else if (_vectorColors.background) {
							colorReplacement.push({
								color: _vectorColors.background.color,
								replace: printEffectColor,
							});
						}

						const foregroundColors = updatedObjectModel._vectorColors.foreground.values();

						for (const foregroundColor of foregroundColors) {
							const replacement = updatedObjectModel.colorReplacement?.find((item) => item.color === foregroundColor.color);

							if (replacement?.replace.real.toLowerCase() !== 'transparent') {
								colorReplacement.push({
									color: foregroundColor.color,
									replace: printEffectColor,
								});
							} else {
								colorReplacement.push({
									color: foregroundColor.color,
									replace: 'transparent',
								});
							}
						}
					} else if (updatedObjectModel.colorReplacement) {
						for (const replacement of updatedObjectModel.colorReplacement) {
							if (replacement.replace.real.toLowerCase() !== 'transparent') {
								colorReplacement.push({
									color: replacement.color,
									replace: (
										replacement.replace.visual
										|| replacement.replace.real
									),
								});
							} else {
								colorReplacement.push({
									color: replacement.color,
									replace: 'transparent',
								});
							}
						}
					}

					let svgWithReplacedColors = svgUtils.replaceColors(
						updatedObjectModel._vectorSVG,
						_vectorColors,
						colorReplacement,
					);

					svgWithReplacedColors = applyVectorSVGPrintEffect(
						svgWithReplacedColors,
						{
							addPrintEffect,
							applyPrintEffect: svgAddPrintEffect,
							printEffect: offeringModel?.printEffect,
						},
					);

					/**
					 * We need to ensure the SVG has the width and height attributes set
					 * according to the object's maxwidth and maxheight properties to
					 * ensure it gets rendered correctly in all scenarios, if not when
					 * doing images manipulations, like in the `CropPhoto` view component,
					 * the scaling might be off due to the lacking of these attributes.
					 */
					const svgOpenTagMatch = svgWithReplacedColors.match(/<svg\b[^>]*>/i);

					if (svgOpenTagMatch) {
						const heightAttribute = `height="${updatedObjectModel.maxheight}"`;
						const widthAttribute = `width="${updatedObjectModel.maxwidth}"`;
						let svgOpenTagWithDimensions = svgOpenTagMatch[0];
						svgOpenTagWithDimensions = (
							svgOpenTagWithDimensions.match(/\sheight\s*=\s*['"][^'"]*['"]/i)
								? svgOpenTagWithDimensions.replace(
									/\sheight\s*=\s*['"][^'"]*['"]/i,
									` ${heightAttribute}`,
								)
								: svgOpenTagWithDimensions.replace(
									'<svg',
									`<svg ${heightAttribute}`,
								)
						);
						svgOpenTagWithDimensions = (
							svgOpenTagWithDimensions.match(/\swidth\s*=\s*['"][^'"]*['"]/i)
								? svgOpenTagWithDimensions.replace(
									/\swidth\s*=\s*['"][^'"]*['"]/i,
									` ${widthAttribute}`,
								)
								: svgOpenTagWithDimensions.replace(
									'<svg',
									`<svg ${widthAttribute}`,
								)
						);
						svgWithReplacedColors = svgWithReplacedColors.replace(
							svgOpenTagMatch[0],
							svgOpenTagWithDimensions,
						);
					}

					updatedObjectModel._finalVectorSVG = svgWithReplacedColors;

					await new Promise<void>((resolve, reject) => {
						const svgBlob = new Blob(
							[svgWithReplacedColors],
							{
								type: 'image/svg+xml;charset=utf-8',
							},
						);
						const svgBase64URL = URL.createObjectURL(svgBlob);
						const img = document.createElement('img');
						img.height = updatedObjectModel.maxheight;
						img.width = updatedObjectModel.maxwidth;
						img.crossOrigin = 'anonymous';
						img.onload = () => {
							if (
								!this._isLatestPageObjectPhotoLoadVersion(
									loadKey,
									loadVersion,
								)
							) {
								URL.revokeObjectURL(svgBase64URL);
								resolve();

								return;
							}

							updatedObjectModel._image = img;
							updatedObjectModel._imageVersion = loadVersion;
							URL.revokeObjectURL(svgBase64URL);
							resolve();
						};
						img.onerror = () => {
							URL.revokeObjectURL(svgBase64URL);
							reject(new Error('Could not load the SVG image'));
						};
						img.src = svgBase64URL;
					});

					if (objectModel._resetImage) {
						updatedObjectModel._resetImage = false;
					}
				}

				if (
					includeMask
					&& updatedObjectModel.mask
					&& updatedObjectModel._image
					&& !updatedObjectModel._canvas
				) {
					const dynamicPhotoScaledUsed = experiment.isFeatureEnabled('flag_dynamic_photo_scaling');
					const maskOptions: LoadObjectMaskOptions = {
						no_store: true,
						resolution,
					};

					if (dynamicPhotoScaledUsed) {
						maskOptions.dynamicWidth = updatedObjectModel._image.width;
					}

					return loadObjectMask(
						updatedObjectModel,
						maskOptions,
					);
				}

				if (
					updatedObjectModel._image
					&& (
						updatedObjectModel._imageVersion === null
						|| typeof updatedObjectModel._imageVersion === 'undefined'
					)
				) {
					updatedObjectModel._imageVersion = loadVersion;
				}

				return updatedObjectModel;
			})
			.then((updatedObjectModel) => {
				if (
					!this._isLatestPageObjectPhotoLoadVersion(
						loadKey,
						loadVersion,
					)
				) {
					return objectModel;
				}

				const effectiveImageVersion = (
					updatedObjectModel._imageVersion
					?? loadVersion
				);

				if (noStore) {
					objectModel._canvas = updatedObjectModel._canvas;
					objectModel._error = 0;
					objectModel._image = updatedObjectModel._image;
					objectModel._imageVersion = effectiveImageVersion;
					objectModel._loading = false;
					objectModel._mask = updatedObjectModel._mask;
					objectModel._finalVectorSVG = updatedObjectModel._finalVectorSVG;
					objectModel._vectorColors = updatedObjectModel._vectorColors;
					objectModel._vectorSVG = updatedObjectModel._vectorSVG;

					if (objectModel._resetImage) {
						objectModel._resetImage = false;
					}
				} else {
					const updates: OptionalExceptFor<PI.PageObjectModel, 'id'> = {
						id: objectModel.id,
						_canvas: updatedObjectModel._canvas,
						_error: 0,
						_image: updatedObjectModel._image,
						_imageVersion: effectiveImageVersion,
						_loading: false,
						_mask: updatedObjectModel._mask,
					};

					if (typeof updatedObjectModel._finalVectorSVG !== 'undefined') {
						(updates as Record<string, unknown>)._finalVectorSVG = updatedObjectModel._finalVectorSVG;
					}

					if (typeof updatedObjectModel._vectorColors !== 'undefined') {
						(updates as Record<string, unknown>)._vectorColors = updatedObjectModel._vectorColors;
					}

					if (typeof updatedObjectModel._vectorSVG !== 'undefined') {
						(updates as Record<string, unknown>)._vectorSVG = updatedObjectModel._vectorSVG;
					}

					if (objectModel._resetImage) {
						updates._resetImage = false;
					}

					this._changePageObject(updates);
				}

				return objectModel;
			})
			.catch((error: Error) => {
				if (
					this._isLatestPageObjectPhotoLoadVersion(
						loadKey,
						loadVersion,
					)
				) {
					const nextError = (objectModel._error ?? 0) + 1;

					if (noStore) {
						objectModel._error = nextError;
						objectModel._image = null;
						objectModel._loading = false;
					} else {
						this._changePageObject({
							id: objectModel.id,
							_error: nextError,
							_image: null,
							_loading: false,
						});
					}
				}

				throw error;
			});

		const trackedPromise = basePromise.finally(() => {
			if (this._pageObjectPhotoLoadPromises.get(loadKey) === trackedPromise) {
				this._pageObjectPhotoLoadPromises.delete(loadKey);
			}
		});

		this._pageObjectPhotoLoadPromises.set(
			loadKey,
			trackedPromise,
		);

		return trackedPromise;
	}

	/**
	 * Applies a page background and keeps every physical book-cover surface in sync.
	 * Background changes on regular pages and non-book products remain page-scoped.
	 *
	 * @param data Page identifier and the complete color-pattern pair to apply.
	 */
	@Action({
		rawError: true,
	})
	public changePageBackground(data: Pick<PI.PageModel, 'id' | 'bgcolor' | 'bgpattern'>): void {
		const pageModel = this.getPage(data.id);
		const productModel = this.getProduct;

		if (
			!pageModel
			|| !productModel
			|| !OfferingGroups(
				productModel.group,
				'BookTypes',
			)
		) {
			this.changePage(data);
			return;
		}

		const coverPageModels: PI.PageModel[] = [];
		const backCoverPageModel = this.getPageByNumber(0);
		const frontCoverPageModel = this.getPageByNumber(1);
		const spinePageModel = this.getSpine;

		if (backCoverPageModel) {
			coverPageModels.push(backCoverPageModel);
		}
		if (spinePageModel) {
			coverPageModels.push(spinePageModel);
		}
		if (frontCoverPageModel) {
			coverPageModels.push(frontCoverPageModel);
		}

		let isCoverPage = false;

		for (const coverPageModel of coverPageModels) {
			if (coverPageModel.id === pageModel.id) {
				isCoverPage = true;
				break;
			}
		}

		if (!isCoverPage) {
			this.changePage(data);
			return;
		}

		for (const coverPageModel of coverPageModels) {
			this.changePage({
				...data,
				id: coverPageModel.id,
			});
		}
	}

	@Action({
		rawError: true,
	})
	public async changePhotoId([
		oldId,
		newId]: [PI.PhotoModel['id'], PI.PhotoModel['id'],
	]): Promise<void> {
		this._changePhotoId([
			oldId,
			newId,
		]);

		// Change the photoid reference for all related pageobject models
		for (const pageObjectModel of this.getObjects) {
			if (pageObjectModel.photoid && pageObjectModel.photoid == oldId) {
				await this.changePageObject({
					id: pageObjectModel.id,
					photoid: newId,
				});
			}
		}
	}

	/**
	 * Updates project settings and propagates shared background values to pages.
	 *
	 * @param data Product settings to apply.
	 */
	@Action({
		rawError: true,
	})
	public changeProductSettings(data: Partial<PI.ProductSettings>): void {
		this._changeProductSettings(data);

		if (
			data.bgcolor
			|| data.bgpattern
		) {
			const pageData: PageBackgroundChange = {};

			if (typeof data.bgcolor !== 'undefined') {
				pageData.bgcolor = data.bgcolor;
			}
			if (typeof data.bgpattern !== 'undefined') {
				pageData.bgpattern = data.bgpattern;
				pageData._bgpattern = null;
			}

			this._changePageBackgrounds(pageData);

			try {
				// Automatically update project content from its offering options
				ProductStateClass.updateContentFromOfferingOptions();
			} catch {
				// Do nothing
			}
		}
	}

	@Action({
		rawError: true,
	})
	public setThinLineDetected(value: boolean): void {
		this._setThinLineDetected(value);
	}

	@Action({
		rawError: true,
	})
	public setThinLineWarningPageIds(pageIds: PI.PageModel['id'][]): void {
		this._setThinLineWarningPageIds(pageIds);
	}

	@Action({
		rawError: true,
	})
	public deselectPageObjects(options?: DeselectePageObjectsModel): void {
		const skipEmptyTextObjectsDeletion = options?.skipEmptyTextObjectsDeletion || false;
		const objectModels = [
			this.getSelectedPageObject,
			this.getSelectedPageObjectForEdition,
		];

		for (const objectModel of objectModels) {
			if (objectModel) {
				if (
					objectModel.type == 'text'
					&& !skipEmptyTextObjectsDeletion
					&& (
						!objectModel.text
						|| objectModel.text.length === 0
					)
				) {
					// Remove the object from the page
					this.removePageObject(objectModel.id);
				} else {
					this._changePageObject({
						id: objectModel.id,
						_crop: false,
						_selected: false,
						_selectedForEdition: false,
					});
				}
			}
		}

		EventBus.emit('deselect');
	}

	@Action({
		rawError: true,
	})
	public fetch({
		productId,
		requestOptions,
		parse,
	}: {
		productId: DB.ProductModel['id'];
		requestOptions?: AxiosRequestConfig;
		parse: boolean;
	}): Promise<DB.ProductModel | API.ProjectDataModel> {
		const defaultRequestOptions: AxiosRequestConfig = {
			method: 'get',
			url: `/api/product/${productId}/all`,
		};

		requestOptions = requestOptions
			? merge(
				defaultRequestOptions,
				requestOptions,
			)
			: defaultRequestOptions;

		// Perform ajax request to get data from server
		return ajax
			.request(
				requestOptions,
				{
					auth: true,
				},
			)
			.then(async (response: AxiosResponse<API.ProjectDataModel>): Promise<DB.ProductModel | API.ProjectDataModel> => {
				const responseData = response.data;

				if (!('product' in responseData)) {
					// Corrupt data
					throw new Error(ERRORS_INVALID_RETURN_DATA);
				}

				const offeringId = parseInt(
					`${responseData.product.group}${responseData.product.typeid}${responseData.product.variantid || '1'}`,
					10,
				);
				const offeringModel = AppDataModule.getOffering(offeringId);
				const offeringModelsSameFlexGroup = (
					!!offeringModel?.flexgroupid
					&& AppDataModule.findOffering({
						flexgroupid: offeringModel.flexgroupid,
					})
				);
				const flexGroupModel = (
					!!offeringModel?.flexgroupid
					&& AppDataModule.flexgroups.find((flexGroup) => flexGroup.id === offeringModel.flexgroupid)
				);

				if (
					flexGroupModel
					&& offeringModelsSameFlexGroup
					&& flexGroupModel.offeringscount !== offeringModelsSameFlexGroup.length
				) {
					await AppDataModule.fetchOfferingsData({
						searchProps: {
							id: offeringId,
							get_from_flexgroupid: true,
						},
					});
				}

				if (!parse) {
					if (responseData.objects) {
						const objectsIds = Object.keys(responseData.objects);

						for (const objectId of objectsIds) {
							const currentObject = responseData.objects[objectId];

							if (currentObject.mask === 'square') {
								currentObject.mask = null;
							}
						}
					}

					return Promise.resolve(responseData);
				}

				return this._parseData({
					data: responseData,
				});
			});
	}

	@Action({
		rawError: true,
	})
	public flagChange(): void {
		this._setSaved(false);
	}

	@Action({
		rawError: true,
	})
	public flagProductTour(tourName: DB.ProductTours): void {
		const props: PI.ProductDataModel['productTours'] = {};
		props[tourName] = true;

		this._setProductTour(props);
	}

	/*
	 * Note: don't change this into a getter method, because the AJAX request needs to be performed every time
	 * (because the returned QR code has a token that can only be used once, and thus not cached/reused)
	 */
	@Action({
		rawError: true,
	})
	public getQRCodeForExternalUpload(): Promise<{ image: string,
		url: string }> {
		if (!this.productId) {
			throw new Error('No product id found');
		}

		return ajax.request(
			{
				url: `/api/product/${this.productId}/qr-upload`,
			},
			{
				auth: true,
			},
		).then((response) => {
			if (!response.data
				|| !response.data.image
				|| !response.data.url
			) {
				throw new Error('Failed to get QR code data');
			}

			return response.data;
		});
	}

	@Action({
		rawError: true,
	})
	public pushHistory(replace = false): void {
		if (this._snapshots.length > this._historyLimit) {
			// Limit size of history to 10 steps back
			this._shiftHistory();
		}

		const newData: PI.ProductDataModel = this.getData;

		if (this._historyIndex >= 0) {
			const oldData = JSON.parse(
				this._snapshots[this._historyIndex],
			) as PI.ProductDataModel;

			if (!equal(
				newData,
				oldData,
			)) {
				if (replace) {
					this._popHistory();
				}

				this._pushHistory(
					newData,
				);
			}
		} else {
			this._pushHistory(
				newData,
			);
		}
	}

	@Action({
		rawError: true,
	})
	private async _findOfferingForProduct(
		productModel: Pick<DB.ProductModel, 'group' | 'typeid' | 'variantid'>,
	): Promise<DB.OfferingModel | undefined> {
		const searchProps: Partial<DB.OfferingModel> = {
			groupid: productModel.group,
			typeid: productModel.typeid,
		};

		if (productModel.variantid) {
			searchProps.variantid = productModel.variantid;
		}

		return AppDataModule.findAndGetOfferingWhere(searchProps);
	}

	@Action({
		rawError: true,
	})
	private async _syncCartItemForHistoryOfferingChange(
		options: {
			previousProduct: DB.ProductModel | null;
			restoredProduct: DB.ProductModel | null;
		},
	): Promise<void> {
		const {
			previousProduct,
			restoredProduct,
		} = options;
		const { productId } = this;

		if (
			!productId
			|| !previousProduct
			|| !restoredProduct
		) {
			return undefined;
		}

		const previousOfferingModel = await this._findOfferingForProduct(previousProduct);
		const restoredOfferingModel = await this._findOfferingForProduct(restoredProduct);

		if (
			!previousOfferingModel
			|| !restoredOfferingModel
			|| previousOfferingModel.id === restoredOfferingModel.id
		) {
			return undefined;
		}

		const cartItem = CartItemsModule.findWhere({
			productid: productId,
			offeringid: previousOfferingModel.id,
		});

		if (!cartItem) {
			return undefined;
		}

		/*
		 * The restored project snapshot must be saved before the cart item points
		 * back to the restored offering, otherwise checkout can pair stale page
		 * dimensions with the updated offering metadata.
		 */
		await ProductStateClass.save();
		await CartItemsModule.putModel({
			id: cartItem.id,
			data: {
				offeringid: restoredOfferingModel.id,
				groupid: restoredOfferingModel.groupid,
				typeid: restoredOfferingModel.typeid,
				variantid: restoredOfferingModel.variantid,
			},
		});

		return undefined;
	}

	@Action({
		rawError: true,
	})
	public redoHistory(offeringChangedCallback?: () => void): Promise<void> {
		return this.restoreFromHistory({
			type: 'redo',
			offeringChangedCallback,
		});
	}

	@Action({
		rawError: true,
	})
	public resetHistory(): void {
		this._resetHistory();
		this.pushHistory();
	}

	@Action({
		rawError: true,
	})
	private async restoreFromHistory(
		options: {
			type: ProductStateHistoryRestoreType;
			offeringChangedCallback?: () => void;
		},
	): Promise<void> {
		const {
			type,
			offeringChangedCallback,
		} = options;
		const currentHistoryData = JSON.parse(JSON.stringify(this.getData));
		const currentProduct = (
			this.getProduct
				? JSON.parse(JSON.stringify(this.getProduct))
				: null
		);
		let restorationFailed = false;
		let historyIndex = this.getHistoryRestoreIndex(type);

		/**
		 * Move back one snapshot before reading the stored state.
		 */
		this._changeHistoryIndex(historyIndex);

		const historyData = JSON.parse(
			this._snapshots[this._historyIndex],
		) as PI.ProductDataModel;
		const { product: productDataToRestore } = historyData;
		const offeringChanged = this.hasProductOfferingChange(
			currentProduct,
			productDataToRestore,
		);

		try {
			if (offeringChanged) {
				/**
				 * Offering changed, trigger callback to allow the caller
				 * to handle any desired action (e.g. open a loader dialog)
				 */
				offeringChangedCallback?.();
			}

			await this.replaceData({
				data: historyData,
			});
			await this._syncThemeAndMaskForHistoryChange({
				previousProduct: currentProduct,
				restoredProduct: productDataToRestore,
			});
			this._setSaved(false);
		} catch {
			/**
			 * Intentionally ignored: restoration failure is
			 * handled by restoring previous state below.
			 */
			restorationFailed = true;
		}

		if (restorationFailed) {
			try {
				/**
				 * Try restore everything back to the previous state
				 * in case data restoration failed midway
				 */
				if (currentProduct) {
					await this.replaceData({
						data: currentHistoryData,
					});
					await this._syncThemeAndMaskForHistoryChange({
						previousProduct: productDataToRestore,
						restoredProduct: currentProduct,
					});

					if (type === 'undo') {
						historyIndex = this._historyIndex + 1;
					} else {
						historyIndex = this._historyIndex - 1;
					}

					this._changeHistoryIndex(historyIndex);
				}
			} catch {
				/**
				 * Swallow error: no action required
				 */
			}

			return;
		}

		if (offeringChanged) {
			/*
			 * History restore replaces product data directly instead of going
			 * through changeOffering, so an undo/redo of an offering change must
			 * explicitly realign the cart item with the restored offering.
			 */
			await this._syncCartItemForHistoryOfferingChange({
				previousProduct: currentProduct,
				restoredProduct: productDataToRestore,
			});
		}
	}

	@Action({
		rawError: true,
	})
	public undoHistory(offeringChangedCallback?: () => void): Promise<void> {
		return this.restoreFromHistory({
			type: 'undo',
			offeringChangedCallback,
		});
	}

	@Action({
		rawError: true,
	})
	public loadMask(): void {
		if (!this._loadingMask) {
			const offeringModel = this.getOffering;

			if (!offeringModel) {
				throw new Error('Could not find offering model');
			}

			if (offeringModel.mask) {
				this._changeLoadingMask(true);

				const image = new Image();

				/*
				 * This enumerated attribute indicates if the fetching of the related image must be done using CORS or not.
				 * CORS-enabled images can be reused in the <canvas> element without being tainted.
				 */
				image.crossOrigin = 'Anonymous';

				image.onload = () => {
					this._setMaskImage(image);
					this._changeLoadingMask(false);
				};
				image.src = offeringModel.mask;
			}
		}
	}

	@Action({
		rawError: true,
	})
	public loadOverlay(): void {
		if (!this._loadingOverlay) {
			const offeringModel = this.getOffering;

			if (!offeringModel) {
				throw new Error('Could not find offering model');
			}

			if (offeringModel.overlay) {
				this._changeLoadingOverlay(true);

				const image = new Image();

				/*
				 * This enumerated attribute indicates if the fetching of the related image must be done using CORS or not.
				 * CORS-enabled images can be reused in the <canvas> element without being tainted.
				 */
				image.crossOrigin = 'Anonymous';

				image.onload = () => {
					this._setOverlayImage(image);
					this._changeLoadingOverlay(false);
				};
				image.src = offeringModel.overlay;
			}
		}
	}

	@Action
	public removeProjectContent({ keepPhotos }: {
		keepPhotos?: boolean;
	}): Promise<void> {
		this.resetPageList([]);
		this._resetPages();
		this._resetObjects();
		this._removeSpine();

		if (!keepPhotos) {
			this.resetPhotos([]);
		}

		return Promise.resolve();
	}

	@Action({
		rawError: true,
	})
	public async removePage({
		pageId,
		keepPhotos,
	}: {
		pageId: PI.PageModel['id'];
		keepPhotos?: boolean;
	}): Promise<void> {
		const pageModel = this.getPage(pageId);

		if (pageModel) { // check if still exists
			const pageIndex = this.getPageIndex(pageModel);
			let backPageModel = null;
			const offeringModel = this.getOffering;
			const pageModels = this.getPages;
			let backPageIndex: number | null = null;

			if (offeringModel) {
				// For prints with back sides, we also need to destroy the back of the page
				if (
					OfferingGroups(
						offeringModel.groupid,
						'DoubleSidePrints',
					)
					|| offeringModel.spread
					|| offeringModel.hasback
				) {
					// Get page number of backside (either the next or the previous page)
					if (
						OfferingGroups(
							offeringModel.groupid,
							'BookTypes',
						)
					) {
						// Check if the theme for this product will have extra pages added after completion
						const extraPages = ThemeStateModule.getFixedPages;

						if (
							offeringModel.fixedcover + offeringModel.startright > 0
							// Book starts with a right page
							&& extraPages.length % 2 === 0
							// Book should end with a left page (would not be the case if one extra page is added to the end of the book after completion)
							&& (
								// This is the first (right) page inside the book (which has no left spread partner)
								pageIndex - offeringModel.fixedcover == 2
								// This is the last (left) page inside the book (which has no right spread partner)
								|| pageIndex == pageModels.length - 1
							)
						) {
							// This page is not part of a spread, so remove just the single page
						} else {
							backPageIndex = (pageIndex + offeringModel.startright) % 2 > 0
								? pageIndex - 1
								: pageIndex + 1;
						}
					} else {
						backPageIndex = pageIndex % 2 > 0 ? pageIndex - 1 : pageIndex + 1;
					}

					if (backPageIndex !== null) {
						// Get backpagestate
						backPageModel = this.getPageByNumber(backPageIndex);
					}
				}

				const objectModels: PI.PageObjectModel[] = this.getPageObjects(pageModel);

				for (const objectModel of objectModels) {
					// referenced object in objectModels array could now be undefined, so check for existince
					if (objectModel) {
						// For Prints, also remove the connection between the product and the photo on this page
						if (
							!keepPhotos
							&& OfferingGroups(
								offeringModel.groupid,
								'PrintTypes',
							)
						) {
							if (objectModel.photoid) {
								const photoId = objectModel.photoid;
								// Make sure there are no other prints in this set that use the same photo (duplicated prints)
								const duplicate = _.find(
									this.objects,
									(oM) => oM.id !== objectModel.id && oM.photoid === photoId,
								);

								if (!duplicate) {
									await this.removePhoto(photoId);
								}
							}
						}

						this.removePageObject(objectModel.id);
					}
				}

				if (backPageModel) {
					const backPageObjectModels: PI.PageObjectModel[] = this.getPageObjects(backPageModel);

					for (const objectModel of backPageObjectModels) {
						// referenced object in objectModels array could now be undefined, so check for existince
						if (objectModel) {
							// For Prints, also removed the connection between the product and the photo on this page
							if (
								!keepPhotos
								&& OfferingGroups(
									offeringModel.groupid,
									'PrintTypes',
								)
							) {
								if (objectModel.photoid) {
									const photoId = objectModel.photoid;
									// Make sure there are no other prints in this set that use the same photo (duplicated prints)
									const duplicate = _.find(
										this.objects,
										(oM) => (
											oM.id !== objectModel.id
											&& oM.photoid === photoId
										),
									);

									if (!duplicate) {
										await this.removePhoto(photoId);
									}
								}
							}

							this.removePageObject(objectModel.id);
						}
					}
				}
			}

			this._removePage(pageId);

			if (backPageModel) {
				this._removePage(backPageModel.id);
			}
		}
	}

	@Action({
		rawError: true,
	})
	public removePhoto(
		photoId: PI.PhotoModel['id'],
	): void {
		const photoModel = this.findPhoto({
			id: photoId,
		});

		if (photoModel && photoModel.id) {
			removePhotoObjects(photoModel.id);

			if (typeof photoModel.id == 'string'
				&& photoModel.externalId
				&& (
					photoModel.source == 'upload'
					|| photoModel.source == 'iOS'
					|| photoModel.source == 'Android'
				)
			) {
				upload.cancelUpload({
					id: photoModel.externalId,
				});
			}

			this._removePhoto(
				photoModel.id,
			);
		} else if (photoId) {
			this._removePhoto(
				photoId,
			);
		}
	}

	@Action({
		rawError: true,
	})
	public replaceData({ data }: {
		data: PI.ProductDataModel;
	}): Promise<void> {
		// Disable auto saving while data is being replaced (to avoid sending incomplete data model)
		this.context.commit(
			'appstate/disableAutoSave',
			undefined,
			{
				root: true,
			},
		);

		return new Promise((resolve, reject) => {
			const currentData: PI.ProductDataModel = this.getData;
			const newData = data;

			if (!currentData.product) {
				reject(new Error('Unknown product model'));
				return;
			}

			if (
				!newData.product?.id
				|| newData.product.id !== currentData.product.id
			) {
				reject(new Error('Unexpected product model'));
				return;
			}

			if (
				!equal(
					currentData.product,
					newData.product,
				)
			) {
				const diff = objDiff(
					currentData.product,
					newData.product,
				);
				this.context.commit(
					'products/updateModel',
					_.extend(
						diff,
						{
							id: newData.product.id,
						},
					),
					{
						root: true,
					},
				);
			}

			if (newData.photoList) {
				/*
				 * If the newData contains photos that are not known to this user session,
				 * we add the models to the productstate store
				 */
				newData.photoList.forEach((photoId, i) => {
					if (typeof photoId === 'number') {
						if (!PhotosModule.getById(photoId)) {
							PhotosModule.fetchModel({
								id: photoId,
								methodOptions: {
									debug: {
										dialog: false,
									},
								},
							}).then(() => {
								if (!currentData.photoList.includes(photoId)) {
									this.addPhoto(
										{
											photoId,
											index: i,
										},
									);
								}
							});
						} else if (!currentData.photoList.includes(photoId)) {
							this.addPhoto(
								{
									photoId,
									index: i,
								},
							);
						}
					}
				});
			}

			if (currentData.photoList) {
				/*
				 * If the currentData has photos that are no longer listed in the newData,
				 * we remove the models from the productstate store
				 */
				currentData.photoList.forEach((photoId) => {
					if (!newData.photoList.includes(photoId)) {
						this._removePhoto(
							photoId,
						);
					}
				});
			}

			if (newData.customAttributes) {
				this.setCustomAttributes(newData.customAttributes);
			}

			if (newData.metaData) {
				this._setMetaData(newData.metaData);
			}

			this._setThinLineDetected(newData.thinLineDetected);

			newData.productAttributes.forEach(
				(attributeData) => {
					const currentAttributeData = _.findWhere(
						currentData.productAttributes,
						{
							id: attributeData.id,
						},
					);

					if (!currentAttributeData) {
						this.addAttribute({
							data: attributeData,
						});
					} else if (!equal(
						attributeData,
						currentAttributeData,
					)) {
						const diff = objDiff(
							currentAttributeData,
							attributeData,
						);
						this.changeProductAttribute({
							data: _.extend(
								diff,
								{
									id: attributeData.id,
								},
							),
						});
					}
				},
			);
			currentData.productAttributes.forEach(
				(attributeData: PI.ProductAttributeModel) => {
					if (!_.findWhere(
						newData.productAttributes,
						{
							id: attributeData.id,
						},
					)) {
						/*
						 * The attribute is no longer present in the newData,
						 * so we remove the model from the productstate store
						 */
						this._removeAttribute(attributeData.id);
					}
				},
			);

			if (
				!currentData.productSettings
				|| !equal(
					newData.productSettings,
					currentData.productSettings,
				)
			) {
				const diff = objDiff(
					currentData.productSettings,
					newData.productSettings,
				);
				this._changeProductSettings(diff);
			}

			const afterObjects = () => {
				if (newData.pages) {
					newData.pageList.map((pageId) => newData.pages[pageId]).forEach(
						(pageData, i) => {
							if (
								pageData.id
								&& !(pageData.id in currentData.pages)
							) {
								/*
								 * If the currentData is missing a page listed in the newData,
								 * we add the model to the productstate store
								 */
								this.addPage({
									data: pageData,
									at: i,
								});
							} else if (
								pageData.id
								&& !equal(
									currentData.pages[pageData.id],
									newData.pages[pageData.id],
								)
							) {
								// If the properties of the page have changed, we update the model
								const diff = objDiff(
									currentData.pages[pageData.id],
									newData.pages[pageData.id],
								);
								this.changePage(
									_.extend(
										diff,
										{
											id: pageData.id,
										},
									),
								);
							}
						},
					);
				}
				if (currentData.pages) {
					currentData.pageList.map((pageId) => currentData.pages[pageId]).forEach(
						(pageData) => {
							if (pageData.id && !(pageData.id in newData.pages)) {
								/*
								 * If the newData does not contain a page listed in the currentData,
								 * we remove the model from the productstate store
								 */
								this._removePage(
									pageData.id,
								);
							}
						},
					);
				}

				if (
					newData.spine
					&& !currentData.spine
				) {
					this.addSpine(newData.spine);
				} else if (
					!newData.spine
					&& currentData.spine
				) {
					this._removeSpine();
				} else if (
					newData.spine
					&& currentData.spine
				) {
					if (newData.spine.id !== currentData.spine.id) {
						this._removeSpine();
						this.addSpine(newData.spine);
					} else if (
						!equal(
							currentData.spine,
							newData.spine,
						)
					) {
						const diff = objDiff(
							currentData.spine,
							newData.spine,
						);
						this.changePage(
							_.extend(
								diff,
								{
									id: newData.spine.id,
								},
							),
						);
					}
				}

				if (newData.pageList) {
					// Order of pages could be different
					this.resetPageList(newData.pageList);
				}

				if (
					newData.address
					|| currentData.address
				) {
					if (
						newData.address
						&& !currentData.address
					) {
						/*
						 * The newData contains an address model not present in the currentData,
						 * we add the model to the productstate store
						 */
						this.addAddress({
							data: data.address,
						});
					} else if (
						!newData.address
						&& currentData.address
					) {
						/*
						 * The currentData contains an address model that is no longer present in the newData,
						 * we remove the model from the productstate store
						 */
						this._removeAddress();
					} else if (
						!equal(
							newData.address,
							currentData.address,
						)
					) {
						/*
						 * The properties in the address model have changed,
						 * update the model in the productstate store
						 */
						const diff = objDiff(
							currentData.address,
							newData.address,
						);
						this.changeAddress(diff);
					}
				}

				if (
					typeof newData.version === 'number'
					&& Number.isFinite(newData.version)
				) {
					this._setVersion(newData.version);
				}

				this._setSaved(true);
				resolve(undefined);
			};

			const newDataObjects: PI.PageObjectModel[] = _.toArray(newData.objects);
			const currentDataObjects: PI.PageObjectModel[] = _.toArray(currentData.objects);
			const objectCount = newDataObjects.length + currentDataObjects.length;

			if (objectCount > 0) {
				const after = _.after(
					objectCount,
					afterObjects,
				);
				const onFail = _.once((err: Error) => {
					this._reset();
					reject(err);
				});

				// Compare the objects in the newData to the ones in the currentData
				newDataObjects.forEach(
					(objectData) => {
						if (
							objectData.id
							&& !(objectData.id in currentData.objects)
						) {
							this
								.addPageObject({
									data: objectData,
								})
								.then(
									() => {
										after();
									},
									onFail,
								);
						} else if (
							objectData.id
							&& !equal(
								currentData.objects[objectData.id],
								newData.objects[objectData.id],
							)
						) {
							const diff = objDiff(
								currentData.objects[objectData.id],
								newData.objects[objectData.id],
							);
							this.changePageObject(
								_.extend(
									diff,
									{
										id: objectData.id,
									},
								),
							);
							after();
						} else {
							after();
						}
					},
				);

				// Compare the objects in the currentData to the ones in the newData
				currentDataObjects.forEach(
					(objectData) => {
						if (
							objectData.id
							&& !(objectData.id in newData.objects)
						) {
							this.removePageObject(objectData.id);
						}

						after();
					},
				);
			} else {
				afterObjects();
			}
		})
			.catch((err) => {
				// Re-enable auto saving while data is being replaced
				this.context.commit(
					'appstate/enableAutoSave',
					undefined,
					{
						root: true,
					},
				);

				throw err;
			})
			.then(() => {
				// Re-enable auto saving while data is being replaced
				this.context.commit(
					'appstate/enableAutoSave',
					undefined,
					{
						root: true,
					},
				);

				return undefined;
			});
	}

	@Action
	public resetMaskImage(): void {
		this._setMaskImage(null);
		this.loadMask();
	}

	@Action
	public resetOverlayImage(): void {
		this._setOverlayImage(null);
		this.loadOverlay();
	}

	@Action
	public reset(): Promise<void> {
		this._reset();
		return Promise.resolve();
	}

	/**
	 * Retry failed photo processing while leaving permanent failures available
	 * for explicit removal. Legacy errors without a retry policy remain eligible.
	 *
	 * @param options Retry controls for the failed-photo collection.
	 * @param options.reset Whether to reset exhausted actor retry budgets.
	 * @returns A promise that settles after every eligible model is handled.
	 */
	@Action({
		rawError: true,
	})
	public async retryPhotoErrors({ reset = false }: { reset?: boolean } = {}): Promise<void> {
		const photoModels: PI.PhotoModel[] = this.getPhotosFailed;

		for (const photoModel of photoModels) {
			if (!isPhotoProcessingErrorRetryable(photoModel)) {
				continue;
			}

			const uploadModel = photoModel.externalId
				? UploadModule.find(photoModel.externalId)
				: undefined;

			/*
			 * A user-initiated retry (reset) must clear the upload actor's
			 * exhausted budget up front, so a subsequent save stops re-throwing
			 * "Upload failure". The full_url / temporary-url recovery paths below
			 * never touch the actor, so without this a processing failure that
			 * already carries a full_url (e.g. from an earlier setTemporaryUploadUrl)
			 * stays wedged in processingFailed with an exhausted budget. Automatic
			 * retries (reset === false) leave the cap in place.
			 */
			if (reset && uploadModel) {
				upload.retry(
					uploadModel,
					{
						reset: true,
					},
				);
			}

			if (photoModel.full_url) {
				try {
					await PhotosModule.retryModel(photoModel.id);
				} catch {
					// Swallow error: no action required
				}
			} else if (photoModel.externalId) {
				if (uploadModel?.url) {
					await PhotosModule.setTemporaryUploadUrl({
						id: photoModel.id,
						url: uploadModel.url,
					});
				} else if (uploadModel) {
					/*
					 * reset === true already reset-and-retried this actor above;
					 * only the automatic (capped) retry remains to be driven here.
					 */
					if (!reset) {
						upload.retry(uploadModel);
					}
				} else {
					await this.removePhoto(photoModel.id);
				}
			} else {
				await this.removePhoto(photoModel.id);
			}
		}
	}

	@Action({
		rawError: true,
	})
	public async save({
		includeArtworkBoundingBoxes,
		requestOptions,
		methodOptions,
	}: {
		includeArtworkBoundingBoxes?: boolean;
		requestOptions?: AxiosRequestConfig;
		methodOptions?: AjaxOptions;
	} = {}): Promise<void> {
		const productId = this.productId;

		if (!productId) {
			throw new Error('Missing required productId');
		}

		const productModel = this.getProduct;

		if (!productModel) {
			const error = new Error('Product model missing before saving snapshot');
			reportError(
				error,
				'error',
				(event) => {
					event.addMetadata(
						'snapshot',
						{
							productId,
							productCollectionSize: ProductsModule.models.length,
							productCollectionIds: ProductsModule.models
								.slice(
									0,
									10,
								)
								.map((model) => model.id),
							pageListLength: this.pageList.length,
							pageModelCount: Object.keys(this.pages).length,
							objectModelCount: Object.keys(this.objects).length,
							photoListLength: this.photoList.length,
							saved: this.getSaved,
							sync: AppStateModule.sync,
							uploadErrors: UploadModule.error.length,
							uploadsInProgress: UploadModule.getInProgress.length,
							photosQueued: this.getPhotosQueued.length,
							url: window.location.href,
						},
					);
				},
			);

			throw error;
		}

		await this._autoFixData();

		/**
		 * Set timestamp to version
		 */
		this._setVersion();

		/**
		 * Get product data
		 */
		const data: PI.ProductDataModel = this.getDataForSnapshot;

		if (includeArtworkBoundingBoxes) {
			const offeringModel = this.getOffering;

			if (!offeringModel) {
				throw new Error('Missing offering model while calculating artwork sizes');
			}

			addArtworkSizesToProductData(
				data,
				offeringModel,
				ConfigModule.metricUnits,
				{
					excludeEmptyPages: AppDataModule.excludeEmptyPages(offeringModel.id),
				},
			);
		}

		if (data.product?.id !== productId) {
			reportError(
				new Error('Snapshot product does not match active product'),
				'error',
				(event) => {
					event.addMetadata(
						'snapshot',
						{
							productId,
							snapshotProductId: data.product?.id,
							snapshotKeys: Object.keys(data),
							productCollectionSize: ProductsModule.models.length,
							productCollectionIds: ProductsModule.models
								.slice(
									0,
									10,
								)
								.map((model) => model.id),
							saved: this.getSaved,
							sync: AppStateModule.sync,
							url: window.location.href,
						},
					);
				},
			);

			throw new Error('Snapshot product does not match active product');
		}

		const defaultRequestOptions: AxiosRequestConfig = {
			method: 'put',
			url: `/api/product/${productId}/snapshot`,
			headers: {
				'content-type': 'application/json; charset=utf-8',
			},
			data,
		};
		const defaultMethodOptions: AjaxOptions = {
			auth: true,
			retry: 1,
			debug: {
				offline: true,
				dialog: true,
				abort: false,
			},
		};

		requestOptions = (
			requestOptions
				? merge(
					defaultRequestOptions,
					requestOptions,
				)
				: defaultRequestOptions
		);
		methodOptions = (
			methodOptions
				? merge(
					defaultMethodOptions,
					methodOptions,
				)
				: defaultMethodOptions
		);

		/*
		 * Set special status 'null', so we can check after saving if there haven't
		 * been any changes since saving started
		 */
		this._setSaved(null);

		const response = await ajax.request(
			requestOptions,
			methodOptions,
		);

		ProductsModule.updateModel(response.data.product);

		if (this.getSaved === null) {
			/*
			 * If saved status is 'false' instead of 'null', we know there's been
			 * changes since starting the save process, so we won't flag it to true
			 */
			this._setSaved(true);
		}
	}

	@Action
	public selectPageObject({
		pageModel,
		objectModelId,
	}: {
		pageModel: PI.PageModel;
		objectModelId: PI.PageObjectModel['id'];
	}): void {
		// Activate this page for editing
		this.setActivePage(pageModel.id);

		// Disable cropping modus on all items
		this.disableCropping(pageModel.id);

		const objectModel = this.getPageObject(objectModelId);

		if (objectModel) {
			const selectedObjectModel = this.getSelectedPageObject;

			if (
				!selectedObjectModel
				|| objectModel.id != selectedObjectModel.id
			) {
				// Deselect all items
				this.deselectPageObjects({
					skipEmptyTextObjectsDeletion: true,
				});

				// Select this object
				this._changePageObject({
					id: objectModel.id,
					_selected: true,
				});

				if (!objectModel.transformable) {
					/*
					 * Object is not transformable, thus can only be moved inside frame
					 * Lock button in object toolbar will therefore not be shown,
					 * so lock position here automatically
					 */
					AppStateModule.setObjectLock();
				} else if (
					objectModel.type == 'photo'
					&& objectModel.x_axis <= 0
					&& objectModel.width >= pageModel.width
				) {
					/*
					 * Photo covers entire page width,
					 * so it is likely that the user wants to move it inside the frame
					 */
					AppStateModule.setObjectLock();
				} else if (
					objectModel.type == 'photo'
					&& objectModel.y_axis <= 0
					&& objectModel.height >= pageModel.height
				) {
					/*
					 * Photo covers entire page height,
					 * so it is likely that the user wants to move it inside the frame
					 */
					AppStateModule.setObjectLock();
				}
			}
		}
	}

	@Action({
		rawError: true,
	})
	public selectPhoto(
		inputData: Partial<PI.PhotoModel>,
	): Promise<PI.PhotoModel> {
		const { productSettings } = this;

		let photoModel: PI.PhotoModel | undefined;

		if (inputData.id
			&& typeof inputData.id === 'string'
			&& inputData._localRef
		) {
			/*
			 * This fixes a bug where the photo model would have been saved to the server already
			 * but the local reference was still the old temporary photo model
			 * this is caused by the changing of the photo id in the project's photoList
			 * and thus the productstate getPhotosSelected would be changed
			 */
			photoModel = PhotosModule.findWhere({
				_localRef: inputData._localRef,
			});
		} else if (inputData.id) {
			photoModel = PhotosModule.getById(inputData.id);
		} else {
			photoModel = PhotosModule.findWhere({
				source: inputData.source,
				externalId: String(inputData.externalId),
			});
		}

		if (photoModel?.id
			&& typeof photoModel.id == 'number'
		) {
			// This photo is already saved to server, so we just update the existing model
			if (inputData.title != photoModel.title) {
				PhotosModule.putModel({
					id: photoModel.id,
					data: {
						title: inputData.title,
					},
				});
			}

			if (photoModel.photodate
				&& productSettings.autoFillMethod == 'photodate'
			) {
				const photoModels = this.getPhotosSelected;
				const index = _.sortedIndex(
					photoModels,
					photoModel,
					'photodate',
				);

				// Add photo model to this product
				this.addPhoto({
					photoId: photoModel.id,
					index,
				});
			} else {
				// Add photo model to this product
				this.addPhoto({
					photoId: photoModel.id,
				});
			}

			return Promise.resolve(photoModel);
		}

		// This is an unsaved model
		return getPhotoSize(inputData)
			.then((photoData) => {
				const localId = photoData.id && typeof photoData.id == 'string'
					? photoData.id
					: photoModel?.id || nanoid();

				const mergedData: Partial<PI.PhotoModel> = {
					...(photoModel || {}),
					...photoData,
				};

				return PhotosModule.addModel({
					_localRef: mergedData._localRef,
					_error: undefined,
					_orientation: mergedData._orientation || undefined,
					_type: mergedData._type || undefined,
					_vectorize: mergedData._vectorize || undefined,
					id: localId,
					userid: this.context.rootState.user.id,
					token: getRandomString(20),
					source: mergedData.source || null,
					externalId: String(mergedData.externalId) || null,
					thumb_url: mergedData.thumb_url || null,
					thumb_width: mergedData.thumb_width || null,
					thumb_height: mergedData.thumb_height || null,
					url: mergedData.url || null,
					width: mergedData.width || null,
					height: mergedData.height || null,
					full_url: mergedData.full_url || null,
					full_width: mergedData.full_width || 0,
					full_height: mergedData.full_height || 0,
					photodate: mergedData.photodate || null,
					title: mergedData.title || null,
					fch: mergedData.fch || undefined,
					fcw: mergedData.fcw || undefined,
					fcx: mergedData.fcx || undefined,
					fcy: mergedData.fcy || undefined,
					type: mergedData.type || 'photo',
				});
			})
			.then((newPhotoModel) => {
				if (newPhotoModel.photodate && productSettings.autoFillMethod == 'photodate') {
					const photoModels = this.getPhotosSelected;
					const index = _.sortedIndex(
						photoModels,
						newPhotoModel,
						'photodate',
					);
					this.addPhoto({
						photoId: newPhotoModel.id,
						index,
					});
				} else {
					this.addPhoto({
						photoId: newPhotoModel.id,
					});
				}

				return newPhotoModel;
			});
	}

	@Action
	public shufflePhotos(): void {
		this.resetPhotos(
			_.shuffle(this.photoList),
		);
	}
}
